import axios from "axios"
import { api } from "@/api/client"
import {
  DailyQuizResponse,
  QuizQuestion,
  RankExamResponse,
  RankExamSubmitResponse,
  VerifyQuizResponse,
} from "../types/academy"
import { DigitalPassport, GameCard, RadarSearchDto, RadarStatus, TrainingFightResult, TrainingStatus, BattleCardBreakdown, BattleCardView, BattleOutcome, OnlinePlayersResponse } from "../types/battle"
import { normalizeColorRank, normalizeRadarStatus, radarStatusQueryParams } from "../utils/radarStatus"
import { normalizeOnlinePlayersResponse } from "../utils/onlinePlayers"
import {
  LocationPointsResponse,
  MapPlayerDetailResponse,
  MapPlayersResponse,
  MapPresenceHeartbeatBody,
  MapPresenceHeartbeatResponse,
  PointHistory,
  ScanLocationResponse,
} from "../types/location"
import type { BoosterRedeemType } from "../utils/redeemPayload"
import {
  mapLocationPoints,
  mapLocationPointsResponse,
  normalizeScanLocationResponse,
  extractLocationPointsPayload,
} from "../utils/locationPoints"
import { normalizeCardElement } from "../utils/cardElement"
import {
  normalizeMapPlayerDetailResponse,
  normalizeMapPlayersResponse,
  normalizeMapPlayer,
} from "../utils/mapPlayers"
import { mapBossPoints, extractBossPointsPayload } from "../utils/bossPoints"
import { normalizePointHistory } from "../utils/pointHistory"
import { BossFightRequest, BossFightResult, BossPointView } from "../types/boss"
import {
  WorldBossActiveResponse,
  WorldBossAttackRequest,
  WorldBossAttackResult,
  WorldBossEventView,
  WorldBossStatus,
} from "../types/worldBoss"
import {
  CardShopCatalog,
  CardShopPoint,
  CardShopPurchaseBody,
  CardShopPurchaseResponse,
} from "../types/cardShop"
import { BoosterShopPoint } from "../types/boosterShop"
import {
  mapCardShopPoints,
  normalizeCardShopCatalog,
  normalizeCardShopPurchaseResponse,
} from "../utils/cardShop"
import { mapBoosterShopPoints } from "../utils/boosterShop"
import {
  MailAcceptContractBody,
  MailClaimContractResponse,
  MailContractActionResult,
  MailContractsMineResponse,
  MailCreateContractBody,
  MailGeoBody,
  MailOpenContractsQuery,
  MailContract,
  PostOfficePoint,
} from "../types/mail"
import {
  mapMailContracts,
  mapPostOfficePoints,
  mailOpenLotExpiresAt,
  normalizeMailClaimResponse,
  normalizeMailContract,
  normalizeMailContractActionResult,
  normalizeMailContractsMine,
} from "../utils/mail"
import {
  CityHallPoint,
  CityHallRaidDetail,
  CityHallRaidGeoBody,
  CityHallRaidStatus,
  CityHallRaidTarget,
  CityHallStatus,
} from "../types/cityHall"
import {
  HotelCheckInBody,
  HotelCheckInResponse,
  HotelCheckOutResponse,
  HotelPoint,
  HotelStatus,
} from "../types/hotel"
import {
  WiseTreeGranted,
  WiseTreeOccupant,
  WiseTreePoint,
  WiseTreeStatus,
} from "../types/wiseTree"
import {
  mapCityHallRaidTargets,
  normalizeCityHallPoint,
  normalizeCityHallRaidDetail,
  normalizeCityHallRaidGeoBody,
  normalizeCityHallRaidStatus,
  normalizeCityHallStatus,
} from "../utils/cityHall"
import {
  mapHotelPoints,
  normalizeHotelCheckInResponse,
  normalizeHotelCheckOutResponse,
  normalizeHotelGeoBody,
  normalizeHotelStatus,
} from "../utils/hotel"
import {
  AirshipBuyResponse,
  AirshipClaimResponse,
  AirshipCityOption,
  AirshipGeoBody,
  AirshipGuardsBody,
  AirshipGuardsResponse,
  AirshipHangarPoint,
  AirshipLaunchBody,
  AirshipLaunchResponse,
  AirshipMapMission,
  AirshipRaidHistoryItem,
  AirshipRaidResult,
  AirshipStatus,
  AirshipUpgradeResponse,
} from "../types/airship"
import {
  mapAirshipCities,
  mapAirshipMissions,
  mapAirshipRaids,
  normalizeAirshipClaimResponse,
  normalizeAirshipGeoBody,
  normalizeAirshipHangarPoint,
  normalizeAirshipMapMission,
  normalizeAirshipRaidResult,
  normalizeAirshipStatus,
} from "../utils/airship"
import {
  BaseAttackBody,
  BaseAttackResult,
  BaseClaimBody,
  BaseClaimResponse,
  BaseConfig,
  BaseGatherResponse,
  BaseGuardsBody,
  BaseGuardsResponse,
  BaseHexesResponse,
  BaseMapBounds,
  BaseMapResponse,
  BaseMineResponse,
  BaseModuleKind,
  BaseRelocateBody,
  BaseRelocateResponse,
  BaseScoutIntel,
  BaseUpgradeBody,
  BaseUpgradeResponse,
  MapPlane,
} from "../types/base"
import {
  normalizeBaseAttackResult,
  normalizeBaseClaimResponse,
  normalizeBaseConfig,
  normalizeBaseGatherResponse,
  normalizeBaseGuardsResponse,
  normalizeBaseHexesResponse,
  normalizeBaseMapResponse,
  normalizeBaseMineResponse,
  normalizeBaseRelocateResponse,
  normalizeBaseScoutIntel,
  normalizeBaseUpgradeResponse,
  normalizeMapPlane,
} from "../utils/base"
import {
  MailMarketActionResponse,
  MailMarketCreateBody,
  MailMarketLotsResponse,
  MailMarketMineResponse,
  MailMarketPurchaseResponse,
} from "../types/mailMarket"
import {
  normalizeMailMarketActionResponse,
  normalizeMailMarketLotsResponse,
  normalizeMailMarketMineResponse,
  normalizeMailMarketPurchaseResponse,
} from "../utils/mailMarket"
import {
  TreasureClaimBody,
  TreasureClaimResponse,
  TreasurePointsResponse,
} from "../types/treasure"
import {
  normalizeTreasureClaimResponse,
  normalizeTreasurePointsResponse,
} from "../utils/treasure"
import {
  BankCardActionResponse,
  BankCoinsActionResponse,
  BankDepositCardBody,
  BankDepositCoinsBody,
  BankGeoBody,
  BankOpenTermDepositBody,
  BankPayFeeResponse,
  BankPoint,
  BankStatus,
  BankTermDeposit,
  BankTermDepositActionResponse,
  BankTermDepositPlans,
  BankUpgradeResponse,
  BankWithdrawCardBody,
  BankWithdrawCoinsBody,
  CardSortBy,
  CardSortDirection,
  ReorderCardsBody,
  SortCardsBody,
  SortCardsResponse,
} from "../types/bank"
import {
  mapBankPoints,
  normalizeBankCardActionResponse,
  normalizeBankCards,
  normalizeBankCoinsActionResponse,
  normalizeBankPayFeeResponse,
  normalizeBankStatus,
  normalizeBankTermDepositActionResponse,
  normalizeBankTermDepositPlans,
  normalizeBankTermDeposits,
  normalizeBankUpgradeResponse,
} from "../utils/bank"
import {
  normalizeCardActionResponse,
  normalizeDigitalPassport,
  normalizeAffiliationPassive,
  normalizeElementStats,
  normalizeGameCard,
  normalizeGameCards,
  normalizePvpStats,
  normalizeSingleCardResponse,
  mergeGameCardUpdate,
} from "../utils/gameCards"
import { resolveAffiliationPassive } from "../constants/affiliationInfo"
import { normalizeBattleCardView } from "../utils/battleCards"
import {
  logPassportAudit,
  setLastRawPassportResponse,
} from "../utils/passportDebug"
import { guardiansLogApiError, guardiansDebugLog } from "../utils/guardiansDebugLog"
import {
  BattleHistoryEntry,
  BattleHistoryMode,
  ClaimDailyQuestResponse,
  ClaimWeeklyQuestResponse,
  DailyQuest,
  DailyQuestKey,
  DailyQuestsResponse,
  CounterattackQuest,
  LeaderboardEntry,
  LeaderboardResponse,
  LeaderboardScope,
  LeaderboardType,
  OnboardingResponse,
  OnboardingStep,
  OnboardingStepKey,
  ShopItem,
  ShopItemsResponse,
  ShopPurchaseResponse,
  ShopRewardType,
  SellCardResponse,
  WeeklyQuest,
  WeeklyQuestKey,
  WeeklyQuestsResponse,
} from "../types/gameMeta"
import { PublicProfile, PublicProfileCard } from "../types/publicProfile"
import { AlmanacView, PublicAlmanacView } from "../types/almanac"
import {
  normalizeAlmanacView,
  normalizePublicAlmanacView,
  resolvePublicProfileAlmanacProgress,
} from "../utils/almanacNormalize"
import { handleAlmanacStampsFromResponse } from "../utils/almanacStampQueue"
import {
  GameReferralStatus,
  ReferralInvitedItem,
  ReferralInvitedResponse,
  ReferralStats,
} from "../types/referral"
import {
  GuildConfig,
  GuildHub,
  GuildListItem,
  GuildMineResponse,
  GuildJoinMode,
  GuildRole,
  GuildTradeCard,
  GuildTradeOffer,
  GuildTradeState,
} from "../types/guild"
import { normalizeQuizCategory } from "../constants/quizCategories"
import { DAILY_QUEST_LABELS, ONBOARDING_STEP_DEFAULTS, ONBOARDING_STEP_ORDER, WEEKLY_QUEST_LABELS } from "../constants/gameMeta"
import { AcademicLeague, CardElement, GuardianPath, PlayStyle } from "../types/battle"
import {
  PetActionResponse,
  PetActiveTimeResponse,
  PetAwakenBody,
  PetChoosePerkBody,
  PetScreenResponse,
} from "../types/pet"
import {
  CityChronicleQuery,
  CityChronicleResponse,
} from "../types/cityChronicle"
import { normalizeCityChronicleResponse } from "../utils/cityChronicleNormalize"
import {
  normalizeActiveTimeProgress,
  pickGuardianRankFromRecord,
  pickGuardianRankInfoFromRecord,
  normalizePetActionResponse,
  normalizePetActiveTimeResponse,
  normalizePetPublicBrief,
  normalizePetScreenResponse,
} from "../utils/petNormalize"
import {
  DominanceRewardChoice,
  FactionTournament,
  GuardianPathInfo,
  LeagueBalance,
  LeagueFinalCandidate,
  TopRewardChoice,
  WarStatus,
} from "../types/faction"
import {
  applyPointControlsToLocationPoints,
  normalizeFactionTournament,
  normalizeGuardianPathInfo,
  normalizeGuardianPathList,
  normalizeLeagueBalance,
  normalizeLeagueFinalCandidate,
  normalizeWarStatus,
} from "../utils/factionNormalize"
import { normalizeGuardianPath } from "../constants/guardianPaths"
import { GameMechanics } from "../types/gameMechanics"
import { normalizeGameMechanics } from "../utils/gameMechanicsNormalize"
import {
  CraftShardResponse,
  CrystallizedResult,
  DustGrant,
  ShardsCatalogResponse,
  ShardsInventoryResponse,
} from "../types/shards"
import {
  normalizeCraftShardResponse,
  normalizeCrystallized,
  normalizeShardsCatalog,
  normalizeShardsInventory,
  parseDustGrant,
} from "../utils/shardsNormalize"
import { calcBaseBS, RANK_EXAM_TOTAL, REFERRAL_REWARD_CHAKRA } from "../constants/game.constants"
import { StarterCardChoice } from "../constants/starterCards"
import { serializeStarterCardsForApi } from "../utils/starterCardChoice"
import {
  getGameApiErrorMessage,
  isCardsUnavailableError,
  isPassportExistsError,
  PassportActionResponse,
  RestartGameRequest,
  SelectLeagueRequest,
  SelectLeagueResponse,
  shouldOpenStarterCardPicker,
} from "./gameApiErrors"
import { normalizePassportActionResponse } from "../utils/gameCards"

type StarterCardsPayload = ReturnType<typeof serializeStarterCardsForApi>

function buildStarterCardsPayload(
  starterCards: [StarterCardChoice, StarterCardChoice]
): { starterCards: StarterCardsPayload } {
  return { starterCards: serializeStarterCardsForApi(starterCards) }
}

function buildSelectLeaguePayload(body: SelectLeagueRequest): {
  league: AcademicLeague
  affiliationElement: CardElement
  guardianPath: GuardianPath
  starterCards: StarterCardsPayload
  nickname?: string
  referrerPublicGameCode?: string
} {
  if (!body.starterCards || body.starterCards.length !== 2) {
    throw new Error("Выберите две стартовые карты")
  }
  if (!body.affiliationElement) {
    throw new Error("Выберите стихию хранителя")
  }
  if (!body.guardianPath) {
    throw new Error("Выберите путь хранителя")
  }
  const payload = {
    league: body.league,
    affiliationElement: body.affiliationElement,
    guardianPath: body.guardianPath,
    ...buildStarterCardsPayload(body.starterCards),
  }
  const nick = body.nickname?.trim()
  const referrer = body.referrerPublicGameCode?.trim()
  return {
    ...payload,
    ...(nick ? { nickname: nick } : {}),
    ...(referrer ? { referrerPublicGameCode: referrer } : {}),
  }
}

function buildRestartPayload(body: RestartGameRequest): {
  affiliationElement: CardElement
  guardianPath: GuardianPath
  starterCards: StarterCardsPayload
} {
  if (!body.starterCards || body.starterCards.length !== 2) {
    throw new Error("Выберите две стартовые карты")
  }
  if (!body.affiliationElement) {
    throw new Error("Выберите стихию хранителя")
  }
  if (!body.guardianPath) {
    throw new Error("Выберите путь хранителя")
  }
  return {
    affiliationElement: body.affiliationElement,
    guardianPath: body.guardianPath,
    ...buildStarterCardsPayload(body.starterCards),
  }
}

export interface CardActionResponse {
  passport: DigitalPassport
  card: GameCard
}

export interface FuseCardsResponse {
  passport: DigitalPassport
  card: GameCard
  /** undefined — старый бэкенд без поля; сравнить ранг до/после на экране */
  fused?: boolean
  dustGrant?: DustGrant | null
  crystallized?: CrystallizedResult | null
}

export interface RedeemCardResponse {
  card: GameCard
  dustGrant?: DustGrant | null
}

export type { BoosterRedeemType }

export interface RedeemBoosterResponse {
  passport: DigitalPassport | null
  amount: number | null
  message: string | null
}

export const getRadarStatus = async (
  params?: RadarSearchDto
): Promise<RadarStatus> => {
  const query = radarStatusQueryParams(params)
  const { data } = await api.get<unknown>("/game/battle/radar-status", {
    params: query,
  })
  return normalizeRadarStatus(data)
}

export const getOnlinePlayers = async (): Promise<OnlinePlayersResponse> => {
  const { data } = await api.get<unknown>("/game/battle/online-players")
  return normalizeOnlinePlayersResponse(data)
}

export const getOnlineCount = async (): Promise<number> => {
  const { data } = await api.get<unknown>("/game/public/online-count")
  const obj = unwrapGamePayload(data)
  return pickNumber(obj.onlineCount ?? obj.online_count)
}

export const getPassport = async (): Promise<DigitalPassport | null> => {
  try {
    const { data } = await api.get<unknown>("/game/passport/me")
    if (data == null || typeof data !== "object" || Array.isArray(data)) {
      return null
    }
    setLastRawPassportResponse(data)
    const normalized = normalizeDigitalPassport(data)
    if (__DEV__ && normalized) {
      logPassportAudit(data, normalized)
    }
    return normalized
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status
      if (status === 404 || (status === 400 && isCardsUnavailableError(error))) {
        return null
      }
    }
    guardiansLogApiError("GET /game/passport/me", error)
    throw error
  }
}

/**
 * Первый старт или дозавершение онбординга.
 * Новый игрок → select-league; битый/частичный паспорт → restart.
 */
export const createStarterGameProfile = async (params: {
  league: AcademicLeague
  affiliationElement: CardElement
  guardianPath: GuardianPath
  firstPick: StarterCardChoice
  secondPick: StarterCardChoice
  nickname?: string
  referrerPublicGameCode?: string
}): Promise<SelectLeagueResponse> => {
  const existing = await getPassport().catch(() => null)
  const hasPartialPassport =
    !!existing?.user_id &&
    (!!existing.academicLeague || !!existing.publicGameCode?.trim())

  if (hasPartialPassport) {
    return restartPassportWithStarterPicks({
      affiliationElement: params.affiliationElement,
      guardianPath: params.guardianPath,
      firstPick: params.firstPick,
      secondPick: params.secondPick,
    })
  }

  try {
    return await selectLeagueWithStarterPicks(params)
  } catch (error) {
    const message = getGameApiErrorMessage(error, "")
    if (isPassportExistsError(message) || shouldOpenStarterCardPicker(error)) {
      return restartPassportWithStarterPicks({
        affiliationElement: params.affiliationElement,
        guardianPath: params.guardianPath,
        firstPick: params.firstPick,
        secondPick: params.secondPick,
      })
    }
    throw error
  }
}

/** Первый старт: league + ровно 2 карты из state (не shorthand с undefined). */
export const selectLeagueWithStarterPicks = async (params: {
  league: AcademicLeague
  affiliationElement: CardElement
  guardianPath: GuardianPath
  firstPick: StarterCardChoice
  secondPick: StarterCardChoice
  nickname?: string
  referrerPublicGameCode?: string
}): Promise<SelectLeagueResponse> => {
  const payload = buildSelectLeaguePayload({
    league: params.league,
    affiliationElement: params.affiliationElement,
    guardianPath: params.guardianPath,
    starterCards: [params.firstPick, params.secondPick],
    nickname: params.nickname,
    referrerPublicGameCode: params.referrerPublicGameCode,
  })
  if (__DEV__) {
    guardiansDebugLog(
      "info",
      "POST /game/academy/select-league",
      JSON.stringify(payload)
    )
  }
  const { data } = await api.post<unknown>(
    "/game/academy/select-league",
    payload
  )
  const { passport, cards } = normalizePassportActionResponse(data)
  if (!passport) throw new Error("Не удалось создать игровой профиль")
  if (__DEV__ && cards.length === 0) {
    guardiansDebugLog(
      "warn",
      "POST /game/academy/select-league",
      "passport ok, cards array empty — check response shape",
      data
    )
  }
  const obj = unwrapGamePayload(data)
  handleAlmanacStampsFromResponse(data)
  return {
    passport,
    cards,
    leagueBalance: normalizeLeagueBalance(obj.leagueBalance ?? obj.league_balance),
    minorityBonusActive: pickBool(
      obj.minorityBonusActive ?? obj.minority_bonus_active
    ),
  }
}

export const selectLeague = async (
  body: SelectLeagueRequest
): Promise<PassportActionResponse> => {
  if (!body.starterCards?.[0] || !body.starterCards?.[1]) {
    throw new Error("Выберите две стартовые карты")
  }
  return selectLeagueWithStarterPicks({
    league: body.league,
    affiliationElement: body.affiliationElement,
    guardianPath: body.guardianPath,
    firstPick: body.starterCards[0],
    secondPick: body.starterCards[1],
    nickname: body.nickname,
  })
}

export const selectPassportAffiliation = async (
  affiliationElement: CardElement
): Promise<DigitalPassport> => {
  const { data } = await api.post<unknown>("/game/passport/affiliation", {
    affiliationElement,
  })
  const passport = normalizeDigitalPassport(data)
  if (!passport) throw new Error("Не удалось сохранить стихию хранителя")
  return passport
}

export const restartPassportWithStarterPicks = async (params: {
  affiliationElement: CardElement
  guardianPath: GuardianPath
  firstPick: StarterCardChoice
  secondPick: StarterCardChoice
}): Promise<PassportActionResponse> => {
  const payload = buildRestartPayload({
    affiliationElement: params.affiliationElement,
    guardianPath: params.guardianPath,
    starterCards: [params.firstPick, params.secondPick],
  })
  if (__DEV__) {
    guardiansDebugLog(
      "info",
      "POST /game/passport/restart",
      JSON.stringify(payload)
    )
  }
  const { data } = await api.post<unknown>("/game/passport/restart", payload)
  const { passport, cards } = normalizePassportActionResponse(data)
  if (!passport) throw new Error("Не удалось начать заново")
  if (__DEV__ && cards.length === 0) {
    guardiansDebugLog(
      "warn",
      "POST /game/passport/restart",
      "passport ok, cards array empty — check response shape",
      data
    )
  }
  handleAlmanacStampsFromResponse(data)
  return { passport, cards }
}

export const updateNickname = async (
  nickname: string
): Promise<DigitalPassport> => {
  const { data } = await api.patch<unknown>("/game/passport/nickname", {
    nickname,
  })
  const passport = normalizeDigitalPassport(data)
  if (!passport) throw new Error("Не удалось обновить никнейм")
  return passport
}

export const setAvatarVariant = async (
  avatarVariant: number
): Promise<DigitalPassport> => {
  const { data } = await api.patch<unknown>("/game/passport/avatar", {
    avatarVariant,
  })
  const passport = normalizeDigitalPassport(data)
  if (!passport) throw new Error("Не удалось обновить аватар")
  return passport
}

export const restartPassport = async (
  body: RestartGameRequest
): Promise<PassportActionResponse> => {
  if (!body.starterCards?.[0] || !body.starterCards?.[1]) {
    throw new Error("Выберите две стартовые карты")
  }
  return restartPassportWithStarterPicks({
    affiliationElement: body.affiliationElement,
    guardianPath: body.guardianPath,
    firstPick: body.starterCards[0],
    secondPick: body.starterCards[1],
  })
}

export const setActiveCard = async (
  cardId: string
): Promise<DigitalPassport> => {
  const { data } = await api.post<unknown>(
    "/game/passport/set-active-card",
    { cardId, card_id: cardId }
  )
  const passport = normalizeDigitalPassport(data)
  if (!passport) throw new Error("Не удалось обновить активную карту")
  return passport
}

export const getCards = async (): Promise<GameCard[]> => {
  try {
    const { data } = await api.get<unknown>("/game/cards")
    const cards = normalizeGameCards(data)
    guardiansDebugLog("info", "GET /game/cards", `parsed ${cards.length} card(s)`)
    if (__DEV__ && cards.length === 0 && data != null) {
      guardiansDebugLog(
        "warn",
        "GET /game/cards",
        "empty after parse — check response wrapper (data/cards array)",
        data
      )
    }
    return cards
  } catch (error) {
    if (isCardsUnavailableError(error)) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      guardiansDebugLog(
        "warn",
        "GET /game/cards",
        `skipped (${status ?? "?"}): ${getGameApiErrorMessage(error, "нет паспорта / лиги")}`
      )
      return []
    }
    guardiansLogApiError("GET /game/cards", error)
    throw error
  }
}

export const redeemCard = async (code: string): Promise<RedeemCardResponse> => {
  const { data } = await api.post<unknown>("/game/cards/redeem", {
    code,
  })
  const obj = unwrapGamePayload(data)
  const card =
    normalizeGameCard(obj.card) ??
    normalizeGameCard(data) ??
    normalizeSingleCardResponse(data)
  if (!card) {
    throw new Error("Некорректный ответ сервера после активации карты")
  }
  handleAlmanacStampsFromResponse(data)
  return {
    card,
    dustGrant: parseDustGrant(obj),
  }
}

export const redeemBooster = async (
  type: BoosterRedeemType,
  code: string
): Promise<RedeemBoosterResponse> => {
  const { data } = await api.post<unknown>(`/game/booster/${type}/redeem`, {
    code,
  })
  const obj = unwrapGamePayload(data)
  const passport =
    normalizeDigitalPassport(obj.passport ?? obj.profile) ?? null
  const amountRaw =
    obj.amount ??
    obj.rewardAmount ??
    obj.reward_amount ??
    obj.coins ??
    obj.chakra ??
    obj.chakraCharges ??
    obj.chakra_charges ??
    obj.energy ??
    obj.fuseBoosters ??
    obj.fuse_boosters ??
    obj.styleChangeTokens ??
    obj.style_change_tokens
  const amount =
    amountRaw == null
      ? null
      : typeof amountRaw === "number" && Number.isFinite(amountRaw)
        ? amountRaw
        : pickNumber(amountRaw) || null
  const message = pickString(obj.message ?? obj.msg ?? obj.text) || null
  return { passport, amount, message }
}

export const spendChakra = async (
  cardId: string
): Promise<CardActionResponse> => {
  const { data } = await api.post<unknown>(
    "/game/cards/spend-chakra",
    { cardId, card_id: cardId }
  )

  const card = normalizeSingleCardResponse(data, cardId)
  if (!card) {
    throw new Error("Некорректный ответ сервера после прокачки")
  }

  const { passport: parsedPassport, card: wrappedCard } = normalizeCardActionResponse(
    data,
    { preferredCardId: cardId }
  )
  const passport = parsedPassport ?? (await getPassport())
  if (!passport) {
    throw new Error("Не удалось обновить паспорт после прокачки")
  }

  return { passport, card: wrappedCard ?? card }
}

function parseFusedFlag(raw: unknown): boolean | undefined {
  if (typeof raw === "boolean") return raw
  if (raw === false || raw === 0 || raw === "0") return false
  if (raw === true || raw === 1 || raw === "1") return true
  return undefined
}

export const fuseCards = async (
  mainCardId: string,
  sacrificeCardId: string,
  options?: { useFuseBooster?: boolean }
): Promise<FuseCardsResponse> => {
  const useFuseBooster = options?.useFuseBooster === true
  const { data } = await api.post<unknown>("/game/cards/fuse", {
    mainCardId,
    main_card_id: mainCardId,
    sacrificeCardId,
    sacrifice_card_id: sacrificeCardId,
    useFuseBooster,
    use_fuse_booster: useFuseBooster,
  })

  let { passport, card } = normalizeCardActionResponse(data, { preferredCardId: mainCardId })

  if (!passport) {
    passport = await getPassport()
  }

  if (!card) {
    const cards = await getCards()
    card = cards.find((c) => c.id === mainCardId) ?? null
  }

  if (!passport || !card) {
    if (__DEV__) {
      console.warn("[fuse] unparsed response", data)
    }
    throw new Error("Некорректный ответ сервера после слияния")
  }

  const payload = unwrapGamePayload(data)
  const fused = parseFusedFlag(payload.fused)

  if (__DEV__ && fused === undefined) {
    console.warn("[fuse] missing fused flag in response", data)
  }

  handleAlmanacStampsFromResponse(data)

  return {
    passport,
    card,
    fused,
    dustGrant: parseDustGrant(payload),
    crystallized: normalizeCrystallized(payload.crystallized),
  }
}

export const changeCardStyle = async (
  cardId: string,
  style: PlayStyle
): Promise<CardActionResponse> => {
  const { data } = await api.post<unknown>("/game/cards/change-style", {
    cardId,
    card_id: cardId,
    style,
  })

  let card = normalizeSingleCardResponse(data, cardId)
  let { passport, card: wrappedCard } = normalizeCardActionResponse(data, {
    preferredCardId: cardId,
  })
  card = wrappedCard ?? card

  if (!passport) {
    passport = await getPassport()
  }

  if (!card) {
    const cards = await getCards()
    card = cards.find((c) => c.id === cardId) ?? null
  }

  if (!passport || !card) {
    if (__DEV__) {
      console.warn("[change-style] unparsed response", data)
    }
    throw new Error("Не удалось сменить стиль карты")
  }

  handleAlmanacStampsFromResponse(data)
  return { passport, card }
}

export const sellCard = async (cardId: string): Promise<SellCardResponse> => {
  const { data } = await api.post<unknown>("/game/cards/sell", {
    cardId,
    card_id: cardId,
  })
  const obj = unwrapGamePayload(data)
  const soldCardId = pickString(obj.soldCardId ?? obj.sold_card_id, cardId)
  const sellPrice = pickNumber(obj.sellPrice ?? obj.sell_price)
  const coins = pickNumber(obj.coins)
  const cardsCount = pickNumber(obj.cardsCount ?? obj.cards_count)
  const cardCapacity = Math.max(
    1,
    pickNumber(obj.cardCapacity ?? obj.card_capacity, 30)
  )
  const slotsLeftRaw = obj.slotsLeft ?? obj.slots_left
  const slotsLeft =
    slotsLeftRaw != null && slotsLeftRaw !== ""
      ? pickNumber(slotsLeftRaw)
      : Math.max(0, cardCapacity - cardsCount)

  return {
    soldCardId,
    sellPrice,
    coins,
    cardsCount,
    cardCapacity,
    slotsLeft,
  }
}

function pickCityTitle(raw: unknown): string | null {
  if (typeof raw === "string") {
    const title = raw.trim()
    return title || null
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>
    const title = pickString(obj.title ?? obj.name ?? obj.cityName ?? obj.city_name)
    return title || null
  }
  return null
}

function normalizePublicProfileCard(raw: unknown): PublicProfileCard | null {
  const card = normalizeGameCard(raw)
  if (!card) return null
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const baseBS = pickNumber(obj.baseBS ?? obj.base_bs, calcBaseBS(card))
  const effectiveBS = pickNumber(obj.effectiveBS ?? obj.effective_bs, baseBS)
  return {
    ...card,
    baseBS,
    effectiveBS,
    isActive: pickBool(obj.isActive ?? obj.is_active),
  }
}

function pickGuardianRank(raw: Record<string, unknown>): number | undefined {
  return pickGuardianRankFromRecord(raw)
}

function normalizePublicProfile(data: unknown): PublicProfile {
  const obj = unwrapGamePayload(data)

  const cards = (Array.isArray(obj.cards) ? obj.cards : [])
    .map(normalizePublicProfileCard)
    .filter((card): card is PublicProfileCard => card != null)

  const activeCardRaw = obj.activeCard ?? obj.active_card
  const activeCard =
    normalizePublicProfileCard(activeCardRaw) ??
    cards.find((card) => card.isActive) ??
    null

  const cityName =
    pickCityTitle(obj.cityName ?? obj.city_name) ??
    pickCityTitle(obj.city) ??
    null

  const guardianPath = normalizeGuardianPath(
    obj.guardianPath ?? obj.guardian_path
  )
  const activeTime = normalizeActiveTimeProgress(
    obj.activeTime ?? obj.active_time
  )
  const guardianRankInfo = pickGuardianRankInfoFromRecord(obj)

  return {
    nickname: pickString(obj.nickname, "Хранитель"),
    publicGameCode: pickString(obj.publicGameCode ?? obj.public_game_code),
    lastActivityAt: pickIsoDate(
      obj.lastActivityAt ?? obj.last_activity_at
    ),
    cityName,
    academicLeague: (() => {
      const raw = pickString(obj.academicLeague ?? obj.academic_league).toUpperCase()
      return raw === "TITAN" || raw === "ATLANT" ? raw : null
    })(),
    guardianPath,
    guardianPathInfo: normalizeGuardianPathInfo(
      obj.guardianPathInfo ?? obj.guardian_path_info,
      guardianPath
    ),
    profileFrameKey:
      pickString(obj.profileFrameKey ?? obj.profile_frame_key) || null,
    globalPts: pickNumber(obj.globalPts ?? obj.global_pts),
    localPts: pickNumber(obj.localPts ?? obj.local_pts),
    cardsCount: pickNumber(obj.cardsCount ?? obj.cards_count, cards.length),
    cardCapacity: (() => {
      const raw = obj.cardCapacity ?? obj.card_capacity
      if (raw == null || raw === "") return undefined
      const value = pickNumber(raw)
      return Number.isFinite(value) && value > 0 ? value : undefined
    })(),
    slotsLeft: (() => {
      const raw = obj.slotsLeft ?? obj.slots_left
      if (raw == null || raw === "") return undefined
      const value = pickNumber(raw)
      return Number.isFinite(value) ? value : undefined
    })(),
    activeCard,
    effectiveBS:
      activeCard?.effectiveBS ??
      (activeCard ? calcBaseBS(activeCard) : undefined),
    cards,
    affiliationElement: (() => {
      const raw = pickString(
        obj.affiliationElement ?? obj.affiliation_element
      ).toUpperCase()
      return raw === "FIRE" ||
        raw === "WATER" ||
        raw === "EARTH" ||
        raw === "LIGHTNING" ||
        raw === "WIND" ||
        raw === "ICE"
        ? raw
        : null
    })(),
    avatarVariant: (() => {
      const raw = obj.avatarVariant ?? obj.avatar_variant
      if (raw == null || raw === "") return 1
      const n = Math.floor(pickNumber(raw, 1))
      return n >= 1 && n <= 3 ? n : 1
    })(),
    affiliationPassive: (() => {
      const elementRaw = pickString(
        obj.affiliationElement ?? obj.affiliation_element
      ).toUpperCase()
      const element =
        elementRaw === "FIRE" ||
        elementRaw === "WATER" ||
        elementRaw === "EARTH" ||
        elementRaw === "LIGHTNING" ||
        elementRaw === "WIND" ||
        elementRaw === "ICE"
          ? elementRaw
          : null
      return resolveAffiliationPassive(
        element,
        normalizeAffiliationPassive(
          obj.affiliationPassive ?? obj.affiliation_passive
        )
      )
    })(),
    pet: normalizePetPublicBrief(obj.pet ?? obj.playerPet ?? obj.player_pet),
    guardianRank:
      guardianRankInfo?.rank ??
      pickGuardianRankFromRecord(obj) ??
      (activeTime?.guardianRank != null && activeTime.guardianRank > 0
        ? activeTime.guardianRank
        : undefined),
    guardianRankInfo,
    pvpStats: normalizePvpStats(obj.pvpStats ?? obj.pvp_stats),
    elementStats: normalizeElementStats(obj.elementStats ?? obj.element_stats),
    almanac: resolvePublicProfileAlmanacProgress(obj),
    guild: (() => {
      const g = obj.guild
      if (!g || typeof g !== "object") return null
      const rec = g as Record<string, unknown>
      const tag = pickString(rec.tag)
      const name = pickString(rec.name)
      if (!tag && !name) return null
      return {
        guildId: pickString(rec.guildId ?? rec.guild_id),
        tag,
        name,
        emblemKey: pickString(rec.emblemKey ?? rec.emblem_key, "gild_1"),
        level: pickNumber(rec.level, 1),
      }
    })(),
  }
}

export const getPublicProfile = async (
  publicGameCode: string
): Promise<PublicProfile> => {
  const { data } = await api.get<unknown>(
    `/game/passport/public/${encodeURIComponent(publicGameCode)}`
  )
  return normalizePublicProfile(data)
}

/** Свой альманах: GET /game/almanac */
export const getAlmanac = async (): Promise<AlmanacView> => {
  const { data } = await api.get<unknown>("/game/almanac")
  return normalizeAlmanacView(data)
}

/** Чужой альманах: GET /game/passport/public/:code/almanac */
export const getPublicAlmanac = async (
  publicGameCode: string
): Promise<PublicAlmanacView> => {
  const { data } = await api.get<unknown>(
    `/game/passport/public/${encodeURIComponent(publicGameCode)}/almanac`
  )
  return normalizePublicAlmanacView(data)
}

function normalizeReferralStatus(raw: unknown): GameReferralStatus {
  const value = pickString(raw, "PENDING").toUpperCase()
  if (value === "REWARDED" || value === "REJECTED") return value
  return "PENDING"
}

function normalizeReferralInvitedItem(
  raw: Record<string, unknown>
): ReferralInvitedItem | null {
  const id = raw.id ?? raw.uuid
  if (id == null) return null

  const referredRaw =
    raw.referred && typeof raw.referred === "object" && !Array.isArray(raw.referred)
      ? (raw.referred as Record<string, unknown>)
      : null

  const rewardedRaw = raw.rewardedAt ?? raw.rewarded_at
  return {
    id: String(id),
    status: normalizeReferralStatus(raw.status),
    createAt: pickString(
      raw.createAt ?? raw.createdAt ?? raw.create_at ?? raw.created_at
    ),
    rewardedAt:
      rewardedRaw == null ? null : pickString(rewardedRaw) || null,
    referred: referredRaw
      ? {
          nickname: pickString(referredRaw.nickname, "Хранитель"),
          publicGameCode: pickString(
            referredRaw.publicGameCode ?? referredRaw.public_game_code
          ),
        }
      : null,
  }
}

function normalizeReferralStats(data: unknown): ReferralStats {
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}

  return {
    publicGameCode: pickString(obj.publicGameCode ?? obj.public_game_code),
    rewardChakra: pickNumber(obj.rewardChakra ?? obj.reward_chakra, REFERRAL_REWARD_CHAKRA),
    monthlyRewardLimit: pickNumber(
      obj.monthlyRewardLimit ?? obj.monthly_reward_limit,
      50
    ),
    monthlyRewardsCount: pickNumber(
      obj.monthlyRewardsCount ?? obj.monthly_rewards_count
    ),
    monthlyRewardsRemaining: pickNumber(
      obj.monthlyRewardsRemaining ?? obj.monthly_rewards_remaining
    ),
    totalInvited: pickNumber(obj.totalInvited ?? obj.total_invited),
    pendingCount: pickNumber(obj.pendingCount ?? obj.pending_count),
    rewardedCount: pickNumber(obj.rewardedCount ?? obj.rewarded_count),
  }
}

function normalizeReferralInvitedResponse(data: unknown): ReferralInvitedResponse {
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const rawItems = Array.isArray(obj.items) ? obj.items : []
  const items = rawItems
    .map((item) =>
      item && typeof item === "object"
        ? normalizeReferralInvitedItem(item as Record<string, unknown>)
        : null
    )
    .filter((item): item is ReferralInvitedItem => item != null)
    .filter((item) => item.status !== "REJECTED")

  const take = pickNumber(obj.take, items.length || 20)
  const skip = pickNumber(obj.skip)
  const total = pickNumber(obj.total, items.length)

  return { items, total, skip, take }
}

export const getReferralStats = async (): Promise<ReferralStats> => {
  const { data } = await api.get<unknown>("/game/referral/stats")
  return normalizeReferralStats(data)
}

export const getReferralInvited = async (params?: {
  skip?: number
  take?: number
}): Promise<ReferralInvitedResponse> => {
  const search = new URLSearchParams()
  if (params?.skip != null) search.set("skip", String(params.skip))
  if (params?.take != null) search.set("take", String(params.take))
  const query = search.toString()
  const { data } = await api.get<unknown>(
    `/game/referral/invited${query ? `?${query}` : ""}`
  )
  return normalizeReferralInvitedResponse(data)
}

export const getGuildConfig = async (): Promise<GuildConfig> => {
  const { data } = await api.get<GuildConfig>("/game/guild/config")
  return data
}

export const getGuildMine = async (): Promise<GuildMineResponse> => {
  const { data } = await api.get<GuildMineResponse>("/game/guild/mine")
  return {
    membership: data?.membership ?? null,
    hub: data?.hub
      ? { ...data.hub, applications: data.hub.applications ?? [] }
      : null,
    pendingApplications: Array.isArray(data?.pendingApplications)
      ? data.pendingApplications
      : [],
  }
}

export const getGuildList = async (): Promise<GuildListItem[]> => {
  const { data } = await api.get<GuildListItem[]>("/game/guild/list")
  return Array.isArray(data) ? data : []
}

export const getGuildCard = async (guildId: string) => {
  const { data } = await api.get(`/game/guild/card/${guildId}`)
  return data as {
    id: string
    name: string
    tag: string
    emblemKey: string
    level: number
    membersCount: number
    maxMembers: number
    joinMode: GuildJoinMode
    cityId: number
  }
}

export const createGuild = async (body: {
  name: string
  tag: string
  emblemKey?: string
  joinMode?: GuildJoinMode
}): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/create", body)
  return data
}

export const renameGuild = async (body: {
  name?: string
  tag?: string
  confirm: boolean
}): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/rename", body)
  return data
}

export const setGuildEmblem = async (emblemKey: string): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/emblem", { emblemKey })
  return data
}

export const setGuildJoinMode = async (
  joinMode: GuildJoinMode
): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/join-mode", { joinMode })
  return data
}

export const setGuildAnnouncement = async (
  announcement: string | null
): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/announcement", {
    announcement,
  })
  return data
}

export const applyToGuild = async (guildId: string, message?: string) => {
  const { data } = await api.post("/game/guild/apply", { guildId, message })
  return data as {
    ok: boolean
    application?: {
      id: string
      guildId: string
      status: string
      message: string | null
    }
  }
}

export const cancelGuildApplication = async (guildId: string) => {
  const { data } = await api.post("/game/guild/application/cancel", { guildId })
  return data as { ok: boolean }
}

export const reviewGuildApplication = async (
  applicationId: string,
  accept: boolean
): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/application/review", {
    applicationId,
    accept,
  })
  return data
}

export const createGuildInvite = async () => {
  const { data } = await api.post<{
    code: string
    expiresAt: string
    maxUses: number
  }>("/game/guild/invite/create")
  return data
}

export const joinGuildByInvite = async (code: string): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/invite/join", { code })
  return data
}

export const leaveGuild = async () => {
  const { data } = await api.post("/game/guild/leave")
  return data
}

export const kickGuildMember = async (targetUserId: number): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/kick", { targetUserId })
  return data
}

export const setGuildMemberRole = async (
  targetUserId: number,
  role: Extract<GuildRole, "OFFICER" | "MEMBER">
): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/role", {
    targetUserId,
    role,
  })
  return data
}

export const transferGuildLeadership = async (
  targetUserId: number
): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/transfer", {
    targetUserId,
  })
  return data
}

export const disbandGuild = async () => {
  const { data } = await api.post("/game/guild/disband")
  return data
}

export const depositGuildBank = async (amount: number): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/bank/deposit", {
    amount,
  })
  return data
}

export const activateGuildBoost = async (boostKey: string): Promise<GuildHub> => {
  const { data } = await api.post<GuildHub>("/game/guild/bank/boost", {
    boostKey,
  })
  return data
}

export const claimGuildChest = async () => {
  const { data } = await api.post<{ coins: number; chakra: number }>(
    "/game/guild/chest/claim"
  )
  return data
}

export const attackGuildBoss = async () => {
  const { data } = await api.post<{
    damage: number
    hp: number
    maxHp: number
    reward: { coins: number; chakra: number } | null
  }>("/game/guild/boss/attack")
  return data
}

export const challengeGuildGvg = async (targetGuildId: string) => {
  const { data } = await api.post("/game/guild/gvg/challenge", { targetGuildId })
  return data
}

export const respondGuildGvg = async (gvgId: string, accept: boolean) => {
  const { data } = await api.post("/game/guild/gvg/respond", { gvgId, accept })
  return data
}

export const createGuildArenaParty = async () => {
  const { data } = await api.post("/game/guild/arena-party/create")
  return data
}

export const joinGuildArenaParty = async () => {
  const { data } = await api.post("/game/guild/arena-party/join")
  return data
}

export const getGuildArenaParty = async () => {
  const { data } = await api.get("/game/guild/arena-party")
  return data as GuildHub["arenaParty"]
}

export const getGuildTrade = async (): Promise<GuildTradeState> => {
  const { data } = await api.get<GuildTradeState>("/game/guild/trade")
  return data
}

export const getGuildTradeCards = async (
  userId: number
): Promise<GuildTradeCard[]> => {
  const { data } = await api.get<GuildTradeCard[]>(
    `/game/guild/trade/cards/${userId}`
  )
  return data
}

export const createGuildTrade = async (body: {
  partnerUserId: number
  giveCoins?: number
  giveChakra?: number
  giveCardId?: string
  wantCoins?: number
  wantChakra?: number
  wantCardId?: string
}): Promise<GuildTradeOffer> => {
  const { data } = await api.post<GuildTradeOffer>("/game/guild/trade/create", body)
  return data
}

export const acceptGuildTrade = async (
  offerId: string
): Promise<GuildTradeOffer> => {
  const { data } = await api.post<GuildTradeOffer>("/game/guild/trade/accept", {
    offerId,
  })
  return data
}

export const declineGuildTrade = async (offerId: string) => {
  const { data } = await api.post("/game/guild/trade/decline", { offerId })
  return data
}

export const cancelGuildTrade = async (offerId: string) => {
  const { data } = await api.post("/game/guild/trade/cancel", { offerId })
  return data
}

export const getLocationPoints = async (): Promise<LocationPointsResponse> => {
  const { data } = await api.get<unknown>("/game/location/points")
  const response = mapLocationPointsResponse(data)
  const raw = extractLocationPointsPayload(data)
  try {
    const war = await getWarStatus()
    if (war?.pointControls?.length) {
      response.points = applyPointControlsToLocationPoints(
        response.points,
        war.pointControls
      )
    }
  } catch {
    // war overlay optional
  }
  if (__DEV__ && raw.length > 0 && response.points.length === 0) {
    console.warn(
      "[location/points] API вернул точки, но парсер их отбросил. Пример:",
      raw[0]
    )
  }
  if (__DEV__) {
    console.log(
      `[location/points] raw=${raw.length} parsed=${response.points.length} cities=${response.cities.length}`
    )
  }
  return response
}

export const getLocationPointHistory = async (
  pointId: string
): Promise<PointHistory> => {
  const { data } = await api.get<unknown>(
    `/game/location/points/${encodeURIComponent(pointId)}/history`
  )
  return normalizePointHistory(data)
}

export const getBossPointHistory = async (
  pointId: string
): Promise<PointHistory> => {
  const { data } = await api.get<unknown>(
    `/game/boss/points/${encodeURIComponent(pointId)}/history`
  )
  return normalizePointHistory(data)
}

export const scanLocation = async (body: {
  pointId: string
  playerLat: number
  playerLng: number
}): Promise<ScanLocationResponse> => {
  const { data } = await api.post<unknown>("/game/location/scan", body)
  const result = normalizeScanLocationResponse(data)
  handleAlmanacStampsFromResponse(data)
  return result
}

function normalizeMapHeartbeatResponse(
  data: unknown
): MapPresenceHeartbeatResponse {
  const obj = unwrapGamePayload(data)
  const playerRaw = obj.player ?? obj.self ?? obj.me
  const player = playerRaw ? normalizeMapPlayer(playerRaw) ?? undefined : undefined
  return {
    ok: pickBool(obj.ok, true),
    expiresInSec: (() => {
      const value = pickNumber(obj.expiresInSec ?? obj.expires_in_sec)
      return Number.isFinite(value) ? value : undefined
    })(),
    visibleOnMap: pickBool(obj.visibleOnMap ?? obj.visible_on_map, true),
    player,
  }
}

export const postMapPresenceHeartbeat = async (
  body: MapPresenceHeartbeatBody
): Promise<MapPresenceHeartbeatResponse> => {
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  if (!Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS")
  }
  const payload: Record<string, number> = {
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
  }
  if (body.accuracyM != null && Number.isFinite(body.accuracyM)) {
    payload.accuracyM = body.accuracyM
  }
  const { data } = await api.post<unknown>("/game/location/heartbeat", payload)
  return normalizeMapHeartbeatResponse(data)
}

export const postMapPresenceLeave = async (): Promise<{ ok: boolean }> => {
  const { data } = await api.post<unknown>("/game/location/leave", {})
  const obj = unwrapGamePayload(data)
  return { ok: pickBool(obj.ok, true) }
}

export const updateMapVisibility = async (
  visibleOnMap: boolean
): Promise<DigitalPassport> => {
  const body = { visibleOnMap, visible_on_map: visibleOnMap }
  const endpoints = [
    "/game/location/visibility",
    "/game/passport/map-visibility",
  ]

  let lastError: unknown = null
  for (const url of endpoints) {
    try {
      const { data } = await api.post<unknown>(url, body)
      const obj = unwrapGamePayload(data)
      const passport =
        normalizeDigitalPassport(data) ??
        normalizeDigitalPassport(obj.passport) ??
        normalizeDigitalPassport(obj.profile)
      if (passport) return passport
      if (pickBool(obj.ok, false)) {
        return { visibleOnMap } as DigitalPassport
      }
      throw new Error("Не удалось обновить видимость на карте")
    } catch (error) {
      lastError = error
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        continue
      }
      throw error
    }
  }

  if (lastError) throw lastError
  throw new Error("Сервер не поддерживает настройку видимости на карте")
}

export const getMapPlayers = async (params: {
  playerLat: number
  playerLng: number
  radiusM?: number
  cityId?: number
}): Promise<MapPlayersResponse> => {
  const playerLat = Number(params.playerLat)
  const playerLng = Number(params.playerLng)
  if (!Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS")
  }
  const query: Record<string, number> = {
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
  }
  if (params.radiusM != null && Number.isFinite(params.radiusM)) {
    query.radiusM = params.radiusM
  }
  if (params.cityId != null && Number.isFinite(params.cityId)) {
    query.cityId = params.cityId
  }
  const { data } = await api.get<unknown>("/game/location/map-players", {
    params: query,
  })
  return normalizeMapPlayersResponse(data)
}

export const getMapPlayerDetail = async (
  userId: number,
  params: { playerLat: number; playerLng: number }
): Promise<MapPlayerDetailResponse> => {
  const playerLat = Number(params.playerLat)
  const playerLng = Number(params.playerLng)
  if (!Number.isFinite(userId) || !Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Некорректные параметры игрока на карте")
  }
  const { data } = await api.get<unknown>(
    `/game/location/map-players/${encodeURIComponent(String(userId))}`,
    {
      params: {
        playerLat: Number(playerLat.toFixed(6)),
        playerLng: Number(playerLng.toFixed(6)),
      },
    }
  )
  const detail = normalizeMapPlayerDetailResponse(data)
  if (!detail) {
    throw new Error("Игрок не найден на карте")
  }
  return detail
}

export const deletePassport = async (): Promise<{ deleted: boolean }> => {
  const { data } = await api.delete<{ deleted: boolean }>("/game/passport/me")
  return data
}

const ONBOARDING_STEP_KEYS: OnboardingStepKey[] = [...ONBOARDING_STEP_ORDER]

const ONBOARDING_STEP_KEY_ALIASES: Record<string, OnboardingStepKey> = {
  LEAGUE_SELECTED: "LEAGUE_SELECTED",
  ACTIVE_CARD_SET: "ACTIVE_CARD_SET",
  ACTIVE_CARD: "ACTIVE_CARD_SET",
  SET_ACTIVE_CARD: "ACTIVE_CARD_SET",
  SET_ACTIVE: "ACTIVE_CARD_SET",
  DAILY_QUIZ_DONE: "DAILY_QUIZ_DONE",
  ACADEMY_QUIZ: "DAILY_QUIZ_DONE",
  DAILY_QUIZ: "DAILY_QUIZ_DONE",
  QUIZ: "DAILY_QUIZ_DONE",
  ACADEMY: "DAILY_QUIZ_DONE",
  LOCATION_SCANNED: "LOCATION_SCANNED",
  SCAN_POINT: "LOCATION_SCANNED",
  LOCATION_SCAN: "LOCATION_SCANNED",
  SCAN: "LOCATION_SCANNED",
  BATTLE_PLAYED: "BATTLE_PLAYED",
  FIRST_BATTLE: "BATTLE_PLAYED",
  BATTLE: "BATTLE_PLAYED",
  SELECT_LEAGUE: "LEAGUE_SELECTED",
}

function resolveOnboardingStepKey(raw: unknown): OnboardingStepKey | null {
  const normalized = pickString(raw).toUpperCase().replace(/[\s-]+/g, "_")
  if (ONBOARDING_STEP_KEYS.includes(normalized as OnboardingStepKey)) {
    return normalized as OnboardingStepKey
  }
  return ONBOARDING_STEP_KEY_ALIASES[normalized] ?? null
}

function unwrapGamePayload(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as Record<string, unknown>
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return obj
}

function extractCompletedOnboardingKeys(
  obj: Record<string, unknown>
): Set<OnboardingStepKey> {
  const completed = new Set<OnboardingStepKey>()
  const lists = [
    obj.completedSteps,
    obj.completed_steps,
    obj.doneSteps,
    obj.done_steps,
    obj.finishedSteps,
    obj.finished_steps,
  ]

  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const item of list) {
      const key =
        typeof item === "string"
          ? resolveOnboardingStepKey(item)
          : item && typeof item === "object"
            ? resolveOnboardingStepKey(
                (item as Record<string, unknown>).key ??
                  (item as Record<string, unknown>).stepKey ??
                  (item as Record<string, unknown>).step_key ??
                  (item as Record<string, unknown>).step
              )
            : null
      if (key) completed.add(key)
    }
  }

  return completed
}

function extractRawOnboardingSteps(obj: Record<string, unknown>): unknown[] {
  if (Array.isArray(obj.steps)) {
    if (obj.steps.every((step) => typeof step === "string")) {
      const completedKeys = new Set(
        obj.steps
          .map((step) => resolveOnboardingStepKey(step))
          .filter((key): key is OnboardingStepKey => key != null)
      )
      return ONBOARDING_STEP_KEYS.map((key) => ({
        key,
        completed: completedKeys.has(key),
      }))
    }
    return obj.steps
  }

  if (Array.isArray(obj.items)) return obj.items
  if (Array.isArray(obj.checklist)) return obj.checklist

  const stepsRecord = obj.steps ?? obj.progress ?? obj.checklist
  if (stepsRecord && typeof stepsRecord === "object" && !Array.isArray(stepsRecord)) {
    return Object.entries(stepsRecord as Record<string, unknown>).map(
      ([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          return { ...(value as Record<string, unknown>), key }
        }
        return { key, completed: value }
      }
    )
  }

  const completedKeys = extractCompletedOnboardingKeys(obj)
  if (completedKeys.size > 0) {
    return ONBOARDING_STEP_KEYS.map((key) => ({
      key,
      completed: completedKeys.has(key),
    }))
  }

  return []
}

function defaultOnboardingStep(key: OnboardingStepKey, completed = false): OnboardingStep {
  const defaults = ONBOARDING_STEP_DEFAULTS[key]
  return {
    key,
    title: defaults.title,
    description: defaults.description,
    completed,
  }
}

function mergeOnboardingSteps(parsed: OnboardingStep[]): OnboardingStep[] {
  const byKey = new Map(parsed.map((step) => [step.key, step]))
  return ONBOARDING_STEP_KEYS.map(
    (key) => byKey.get(key) ?? defaultOnboardingStep(key)
  )
}

const DAILY_QUEST_KEYS: DailyQuestKey[] = [
  "DAILY_QUIZ",
  "WIN_BATTLE",
  "SCAN_POINT",
  "WIN_TRAINING",
  "CLAIM_TREASURE",
  "PERFECT_QUIZ",
]

const WEEKLY_QUEST_KEYS: WeeklyQuestKey[] = [
  "WIN_5_BATTLES",
  "WIN_3_ELEMENTS",
  "DEFEAT_BOSS",
  "DEAL_WORLD_BOSS_DAMAGE",
  "LEVEL_UP_CARDS",
  "SUCCESSFUL_FUSE",
  "EARN_WAR_PTS",
  "SCAN_30_POINTS",
]

const BATTLE_HISTORY_MODES: BattleHistoryMode[] = [
  "RADAR",
  "QR",
  "CHALLENGE",
  "DECK_RADAR",
  "DECK_CHALLENGE",
]

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1") return true
  if (value === 0 || value === "0") return false
  return fallback
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickIsoDate(value: unknown): string | null {
  if (value == null || value === "") return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString()
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
  const raw = pickString(value).trim()
  if (!raw) return null
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function pickCompleted(raw: Record<string, unknown>): boolean {
  const explicit =
    raw.completed ??
    raw.isCompleted ??
    raw.is_completed ??
    raw.done ??
    raw.isDone ??
    raw.is_done
  if (explicit != null) return pickBool(explicit)

  const status = pickString(raw.status ?? raw.state).toLowerCase()
  if (status) {
    if (
      status.includes("complete") ||
      status.includes("done") ||
      status.includes("finished")
    ) {
      return true
    }
    if (status.includes("pending") || status.includes("todo")) {
      return false
    }
  }

  const completedAt = raw.completedAt ?? raw.completed_at
  if (completedAt == null) return false
  if (typeof completedAt === "string") return completedAt.trim().length > 0
  return true
}

function normalizeOnboardingStep(raw: Record<string, unknown>): OnboardingStep | null {
  const key = resolveOnboardingStepKey(
    raw.key ??
      raw.stepKey ??
      raw.step_key ??
      raw.step ??
      raw.code ??
      raw.name ??
      raw.id
  )
  if (!key) return null
  const defaults = ONBOARDING_STEP_DEFAULTS[key]
  return {
    key,
    title: pickString(raw.title ?? raw.label, defaults.title),
    description:
      pickString(raw.description ?? raw.desc, defaults.description) || undefined,
    completed: pickCompleted(raw),
  }
}

function normalizeCounterattackQuest(
  raw: Record<string, unknown> | null | undefined
): CounterattackQuest | null {
  if (!raw || typeof raw !== "object") return null
  const key = pickString(raw.questKey ?? raw.quest_key ?? raw.key).toUpperCase()
  const isCounterattack =
    pickBool(raw.isCounterattack ?? raw.is_counterattack) ||
    key === "COUNTERATTACK"
  if (!isCounterattack) return null

  const progress = pickNumber(raw.progress)
  const target = pickNumber(raw.target, 1)
  const completed =
    pickBool(raw.completed ?? raw.isCompleted ?? raw.is_completed) ||
    (target > 0 && progress >= target)

  return {
    questKey: "COUNTERATTACK",
    title: pickString(raw.title, "Контратака"),
    description:
      pickString(raw.description, "Заработайте 1 war pt за неделю") ||
      undefined,
    completed,
    claimed: pickBool(
      raw.claimed ??
        raw.isClaimed ??
        raw.is_claimed ??
        raw.rewardClaimed ??
        raw.reward_claimed
    ),
    isCounterattack: true,
    progress: target > 0 ? progress : undefined,
    target: target > 1 ? target : 1,
    rewardCoins: pickNumber(raw.rewardCoins ?? raw.reward_coins) || undefined,
    rewardChakra: pickNumber(raw.rewardChakra ?? raw.reward_chakra) || undefined,
  }
}

function normalizeDailyQuest(raw: Record<string, unknown>): DailyQuest | null {
  if (!raw || typeof raw !== "object") return null
  const key = pickString(raw.questKey ?? raw.quest_key ?? raw.key).toUpperCase()
  if (!key || !DAILY_QUEST_KEYS.includes(key as DailyQuestKey)) return null
  const questKey = key as DailyQuestKey
  const defaults = DAILY_QUEST_LABELS[questKey]
  if (!defaults) return null
  const progress = pickNumber(raw.progress)
  const target = pickNumber(raw.target, 1)
  const completed =
    pickBool(raw.completed ?? raw.isCompleted ?? raw.is_completed) ||
    (target > 0 && progress >= target)

  return {
    questKey,
    title: pickString(raw.title, defaults.title),
    description: pickString(raw.description, defaults.description) || undefined,
    progress: target > 0 ? progress : undefined,
    target: target > 0 ? target : undefined,
    completed,
    claimed: pickBool(
      raw.claimed ??
        raw.isClaimed ??
        raw.is_claimed ??
        raw.rewardClaimed ??
        raw.reward_claimed
    ),
    rewardCoins: pickNumber(raw.rewardCoins ?? raw.reward_coins, defaults.rewardCoins),
    rewardChakra: pickNumber(
      raw.rewardChakra ?? raw.reward_chakra ?? raw.rewardCharges ?? raw.reward_charges,
      defaults.rewardChakra
    ),
  }
}

function normalizeClaimDailyQuestResponse(
  data: unknown,
  questKey: DailyQuestKey
): ClaimDailyQuestResponse {
  const obj = unwrapGamePayload(data)
  const passport = normalizeDigitalPassport(
    obj.passport ?? obj.profile ?? obj.digitalPassport ?? obj.digital_passport
  )
  const resolvedKey = pickString(obj.questKey ?? obj.quest_key, questKey).toUpperCase()
  const normalizedKey = DAILY_QUEST_KEYS.includes(resolvedKey as DailyQuestKey)
    ? (resolvedKey as DailyQuestKey)
    : questKey
  const defaults = DAILY_QUEST_LABELS[normalizedKey]

  // Новый формат claim: { rewardCoins, rewardChakra, quests }
  // Старый: массив квестов — берём defaults / совпадение по ключу.
  let rewardCoins = pickNumber(obj.rewardCoins ?? obj.reward_coins, NaN)
  let rewardChakra = pickNumber(
    obj.rewardChakra ?? obj.reward_chakra ?? obj.rewardCharges ?? obj.reward_charges,
    NaN
  )
  if (!Number.isFinite(rewardCoins) || !Number.isFinite(rewardChakra)) {
    const list = Array.isArray(obj)
      ? obj
      : Array.isArray(obj.quests)
        ? obj.quests
        : Array.isArray(data)
          ? data
          : []
    const match = list.find((item) => {
      if (!item || typeof item !== "object") return false
      const key = pickString(
        (item as Record<string, unknown>).questKey ??
          (item as Record<string, unknown>).quest_key
      ).toUpperCase()
      return key === normalizedKey
    }) as Record<string, unknown> | undefined
    if (match) {
      rewardCoins = pickNumber(match.rewardCoins ?? match.reward_coins, defaults.rewardCoins)
      rewardChakra = pickNumber(
        match.rewardChakra ?? match.reward_chakra,
        defaults.rewardChakra
      )
    } else {
      rewardCoins = defaults.rewardCoins
      rewardChakra = defaults.rewardChakra
    }
  }

  return {
    passport: passport ?? undefined,
    questKey: normalizedKey,
    rewardCoins,
    rewardChakra,
  }
}

function normalizeShopItem(raw: Record<string, unknown>): ShopItem | null {
  const id = raw.id ?? raw.uuid ?? raw.itemId ?? raw.item_id
  if (id == null) return null
  const rewardTypeRaw = pickString(raw.rewardType ?? raw.reward_type, "ENERGY").toUpperCase()
  const rewardType: ShopRewardType =
    rewardTypeRaw === "CHAKRA"
      ? "CHAKRA"
      : rewardTypeRaw === "COINS"
        ? "COINS"
        : rewardTypeRaw === "STYLE_CHANGE_TOKEN"
          ? "STYLE_CHANGE_TOKEN"
          : rewardTypeRaw === "FUSE_BOOSTER"
            ? "FUSE_BOOSTER"
            : rewardTypeRaw === "SHARD_DUST"
              ? "SHARD_DUST"
              : rewardTypeRaw === "CARD_CAPACITY"
                ? "CARD_CAPACITY"
                : rewardTypeRaw === "PROFILE_COSMETIC"
                  ? "PROFILE_COSMETIC"
                  : "ENERGY"
  const elementRaw = pickString(raw.rewardElement ?? raw.reward_element).toUpperCase()
  const rewardElement =
    elementRaw === "FIRE" ||
    elementRaw === "WATER" ||
    elementRaw === "EARTH" ||
    elementRaw === "LIGHTNING" ||
    elementRaw === "WIND" ||
    elementRaw === "ICE"
      ? (elementRaw as CardElement)
      : null
  return {
    id: String(id),
    title: pickString(raw.title ?? raw.name, "Товар"),
    description: pickString(raw.description ?? raw.desc) || null,
    priceCoins: pickNumber(raw.priceCoins ?? raw.price_coins ?? raw.price),
    rewardType,
    rewardAmount: pickNumber(raw.rewardAmount ?? raw.reward_amount ?? raw.amount),
    sortOrder: pickNumber(raw.sortOrder ?? raw.sort_order, 0) || undefined,
    isActive: pickBool(raw.isActive ?? raw.is_active, true),
    slug: pickString(raw.slug) || null,
    rewardElement,
  }
}

function normalizeOnboarding(data: unknown): OnboardingResponse {
  const obj = unwrapGamePayload(data)
  const rawSteps = extractRawOnboardingSteps(obj)
  let steps = rawSteps
    .map((step) => {
      if (typeof step === "string") {
        const key = resolveOnboardingStepKey(step)
        return key ? defaultOnboardingStep(key, true) : null
      }
      return step && typeof step === "object"
        ? normalizeOnboardingStep(step as Record<string, unknown>)
        : null
    })
    .filter((step): step is OnboardingStep => step != null)

  if (steps.length === 0) {
    const completedKeys = extractCompletedOnboardingKeys(obj)
    steps = ONBOARDING_STEP_KEYS.map((key) =>
      defaultOnboardingStep(key, completedKeys.has(key))
    )
  } else {
    steps = mergeOnboardingSteps(steps)
  }

  const completedCount = pickNumber(
    obj.completedCount ?? obj.completed_count,
    steps.filter((s) => s.completed).length
  )
  const totalSteps = pickNumber(
    obj.totalSteps ?? obj.total_steps ?? obj.totalCount ?? obj.total_count,
    steps.length || ONBOARDING_STEP_KEYS.length
  )
  const hasPassport = pickBool(obj.hasPassport ?? obj.has_passport, false)
  const isCompleteRaw = pickBool(
    obj.isComplete ?? obj.is_complete ?? obj.allCompleted ?? obj.all_completed,
    hasPassport && completedCount >= totalSteps && totalSteps > 0
  )

  if (__DEV__ && rawSteps.length > 0 && steps.every((step) => !step.completed)) {
    console.warn("[onboarding] steps parsed but none marked completed", {
      rawSteps,
      completedCount,
    })
  }

  return {
    steps,
    completedCount,
    totalSteps,
    hasPassport,
    isComplete: hasPassport && isCompleteRaw,
  }
}

function normalizeDailyQuests(data: unknown): DailyQuestsResponse {
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const rawQuests = Array.isArray(obj.quests)
    ? obj.quests
    : Array.isArray(data)
      ? data
      : []

  let counterattack: CounterattackQuest | null = null
  if (obj.counterattack && typeof obj.counterattack === "object") {
    counterattack = normalizeCounterattackQuest(
      obj.counterattack as Record<string, unknown>
    )
  }
  if (!counterattack && obj.counterAttack && typeof obj.counterAttack === "object") {
    counterattack = normalizeCounterattackQuest(
      obj.counterAttack as Record<string, unknown>
    )
  }

  const quests: DailyQuest[] = []
  for (const quest of rawQuests) {
    if (!quest || typeof quest !== "object") continue
    const row = quest as Record<string, unknown>
    const extra = normalizeCounterattackQuest(row)
    if (extra) {
      counterattack = extra
      continue
    }
    const daily = normalizeDailyQuest(row)
    if (daily) quests.push(daily)
  }

  return {
    quests,
    counterattack,
    resetAt: pickString(obj.resetAt ?? obj.reset_at) || null,
  }
}

function normalizeWeeklyQuest(raw: Record<string, unknown>): WeeklyQuest | null {
  const key = pickString(raw.questKey ?? raw.quest_key ?? raw.key).toUpperCase()
  if (!WEEKLY_QUEST_KEYS.includes(key as WeeklyQuestKey)) return null
  const questKey = key as WeeklyQuestKey
  const defaults = WEEKLY_QUEST_LABELS[questKey]
  const progress = pickNumber(raw.progress)
  const target = pickNumber(raw.target, defaults.target)
  const completed =
    pickBool(raw.completed ?? raw.isCompleted ?? raw.is_completed) ||
    (target > 0 && progress >= target)

  return {
    questKey,
    title: pickString(raw.title, defaults.title),
    description: pickString(raw.description, defaults.description) || undefined,
    progress,
    target,
    completed,
    claimed: pickBool(
      raw.claimed ??
        raw.isClaimed ??
        raw.is_claimed ??
        raw.rewardClaimed ??
        raw.reward_claimed
    ),
    rewardCoins: pickNumber(raw.rewardCoins ?? raw.reward_coins, defaults.rewardCoins),
    rewardChakra: pickNumber(
      raw.rewardChakra ?? raw.reward_chakra ?? raw.rewardCharges ?? raw.reward_charges,
      defaults.rewardChakra
    ),
  }
}

function normalizeWeeklyQuests(data: unknown): WeeklyQuestsResponse {
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const rawQuests = Array.isArray(obj.quests)
    ? obj.quests
    : Array.isArray(data)
      ? data
      : []
  const quests = rawQuests
    .map((quest) =>
      quest && typeof quest === "object"
        ? normalizeWeeklyQuest(quest as Record<string, unknown>)
        : null
    )
    .filter((quest): quest is WeeklyQuest => quest != null)

  return {
    quests,
    resetAt: pickString(obj.resetAt ?? obj.reset_at) || null,
  }
}

function normalizeClaimWeeklyQuestResponse(
  data: unknown,
  questKey: WeeklyQuestKey
): ClaimWeeklyQuestResponse {
  const obj = unwrapGamePayload(data)
  const passport = normalizeDigitalPassport(
    obj.passport ?? obj.profile ?? obj.digitalPassport ?? obj.digital_passport
  )
  const resolvedKey = pickString(obj.questKey ?? obj.quest_key, questKey).toUpperCase()
  const normalizedKey = WEEKLY_QUEST_KEYS.includes(resolvedKey as WeeklyQuestKey)
    ? (resolvedKey as WeeklyQuestKey)
    : questKey
  const defaults = WEEKLY_QUEST_LABELS[normalizedKey]

  let rewardCoins = pickNumber(obj.rewardCoins ?? obj.reward_coins, NaN)
  let rewardChakra = pickNumber(
    obj.rewardChakra ?? obj.reward_chakra ?? obj.rewardCharges ?? obj.reward_charges,
    NaN
  )
  if (!Number.isFinite(rewardCoins) || !Number.isFinite(rewardChakra)) {
    const list = Array.isArray(obj)
      ? obj
      : Array.isArray(obj.quests)
        ? obj.quests
        : Array.isArray(data)
          ? data
          : []
    const match = list.find((item) => {
      if (!item || typeof item !== "object") return false
      const key = pickString(
        (item as Record<string, unknown>).questKey ??
          (item as Record<string, unknown>).quest_key
      ).toUpperCase()
      return key === normalizedKey
    }) as Record<string, unknown> | undefined
    if (match) {
      rewardCoins = pickNumber(match.rewardCoins ?? match.reward_coins, defaults.rewardCoins)
      rewardChakra = pickNumber(
        match.rewardChakra ?? match.reward_chakra,
        defaults.rewardChakra
      )
    } else {
      rewardCoins = defaults.rewardCoins
      rewardChakra = defaults.rewardChakra
    }
  }

  return {
    passport: passport ?? undefined,
    questKey: normalizedKey,
    rewardCoins,
    rewardChakra,
  }
}

function normalizeBattleHistoryMode(value: unknown): BattleHistoryMode {
  const raw = pickString(value).toUpperCase()
  if (BATTLE_HISTORY_MODES.includes(raw as BattleHistoryMode)) {
    return raw as BattleHistoryMode
  }
  return "RADAR"
}

function normalizeBattleHistoryRound(raw: unknown): import("../types/gameMeta").BattleHistoryRoundEntry | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const round = pickNumber(obj.round, NaN)
  if (!Number.isFinite(round)) return null
  const myCard = normalizeGameCard(obj.myCard ?? obj.my_card)
  const opponentCard = normalizeGameCard(obj.opponentCard ?? obj.opponent_card)
  return {
    round,
    outcome: normalizeBattleOutcome(obj.outcome ?? obj.roundOutcome ?? obj.round_outcome),
    myCard: myCard ?? undefined,
    opponentCard: opponentCard ?? undefined,
    myCombat: normalizeBattleCardBreakdown(obj.myCombat ?? obj.my_combat),
    opponentCombat: normalizeBattleCardBreakdown(
      obj.opponentCombat ?? obj.opponent_combat
    ),
  }
}

function normalizeBattleHistoryEntry(raw: unknown): BattleHistoryEntry | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const id = pickString(obj.id ?? obj.uuid)
  if (!id) return null

  const myCard = normalizeGameCard(obj.myCard ?? obj.my_card)
  const opponentCard = normalizeGameCard(obj.opponentCard ?? obj.opponent_card)
  if (!myCard || !opponentCard) return null

  const roundsRaw = Array.isArray(obj.rounds) ? obj.rounds : []
  const rounds = roundsRaw
    .map(normalizeBattleHistoryRound)
    .filter((r): r is NonNullable<typeof r> => r != null)

  return {
    id,
    battleId: pickString(obj.battleId ?? obj.battle_id),
    mode: normalizeBattleHistoryMode(obj.mode),
    opponentNickname: pickString(
      obj.opponentNickname ?? obj.opponent_nickname ?? obj.opponentName,
      "Соперник"
    ),
    outcome: normalizeBattleOutcome(obj.outcome),
    ptsDelta: pickNumber(obj.ptsDelta ?? obj.pts_delta),
    coinsDelta: pickNumber(obj.coinsDelta ?? obj.coins_delta),
    myCard,
    opponentCard,
    myCombat: normalizeBattleCardBreakdown(obj.myCombat ?? obj.my_combat),
    opponentCombat: normalizeBattleCardBreakdown(
      obj.opponentCombat ?? obj.opponent_combat
    ),
    playedAt: pickString(obj.playedAt ?? obj.played_at ?? obj.createdAt),
    matchScore: pickString(obj.matchScore ?? obj.match_score) || undefined,
    colorRank: normalizeColorRank(obj.colorRank ?? obj.color_rank) ?? undefined,
    rounds: rounds.length ? rounds : undefined,
  }
}

function normalizeBattleHistory(data: unknown): BattleHistoryEntry[] {
  const obj = unwrapGamePayload(data)
  const rawList = Array.isArray(obj)
    ? obj
    : Array.isArray(obj.items)
      ? obj.items
      : Array.isArray(obj.history)
        ? obj.history
        : Array.isArray(obj.entries)
          ? obj.entries
          : Array.isArray(data)
            ? data
            : []
  return rawList
    .map(normalizeBattleHistoryEntry)
    .filter((entry): entry is BattleHistoryEntry => entry != null)
}

function pickLeaderboardAffiliationElement(
  row: Record<string, unknown>
): CardElement | null {
  const sources: unknown[] = [
    row,
    row.player,
    row.passport,
    row.profile,
    row.user,
  ]

  for (const source of sources) {
    if (!source || typeof source !== "object" || Array.isArray(source)) continue
    const obj = source as Record<string, unknown>
    const activeCard = obj.activeCard ?? obj.active_card
    const fromCard =
      activeCard && typeof activeCard === "object" && !Array.isArray(activeCard)
        ? (activeCard as Record<string, unknown>).element
        : null
    const element = normalizeCardElement(
      obj.affiliationElement ??
        obj.affiliation_element ??
        obj.element ??
        fromCard
    )
    if (element) return element
  }

  return null
}

function normalizeLeaderboardEntry(
  row: Record<string, unknown>,
  index: number,
  scope: LeaderboardScope
): LeaderboardEntry | null {
  const ptsRaw =
    scope === "global"
      ? row.globalPts ?? row.global_pts ?? row.pts ?? row.points
      : row.localPts ?? row.local_pts ?? row.pts ?? row.points
  const warPtsRaw = row.warPts ?? row.war_pts
  const cardsCountRaw = row.cardsCount ?? row.cards_count
  const leagueRaw = pickString(row.academicLeague ?? row.academic_league).toUpperCase()
  const guardianPathRaw = pickString(row.guardianPath ?? row.guardian_path).toUpperCase()

  return {
    rank: pickNumber(row.rank ?? row.position, index + 1),
    nickname: pickString(row.nickname ?? row.name, "—"),
    publicGameCode: pickString(row.publicGameCode ?? row.public_game_code) || undefined,
    pts: pickNumber(ptsRaw),
    warPts: warPtsRaw == null ? undefined : pickNumber(warPtsRaw),
    cardsCount:
      cardsCountRaw == null || cardsCountRaw === ""
        ? undefined
        : pickNumber(cardsCountRaw),
    cityName:
      pickCityTitle(row.cityName ?? row.city_name) ??
      pickCityTitle(row.city) ??
      null,
    academicLeague:
      leagueRaw === "TITAN" || leagueRaw === "ATLANT"
        ? (leagueRaw as AcademicLeague)
        : null,
    guardianPath:
      guardianPathRaw === "STRAZH" ||
      guardianPathRaw === "SLEDOPYT" ||
      guardianPathRaw === "MASTER" ||
      guardianPathRaw === "TAKTIK" ||
      guardianPathRaw === "NASTAVNIK" ||
      guardianPathRaw === "OKHOTNIK"
        ? (guardianPathRaw as import("../types/battle").GuardianPath)
        : null,
    affiliationElement: pickLeaderboardAffiliationElement(row),
    avatarVariant: (() => {
      const raw = row.avatarVariant ?? row.avatar_variant
      if (raw == null || raw === "") return 1
      const n = Math.floor(pickNumber(raw, 1))
      return n >= 1 && n <= 3 ? n : 1
    })(),
    profileFrameKey:
      pickString(row.profileFrameKey ?? row.profile_frame_key) || null,
    guardianRank: pickGuardianRank(row),
    isMe: pickBool(row.isMe ?? row.is_me ?? row.isCurrentUser ?? row.is_current_user),
    guildTag: (() => {
      const g = row.guild
      if (g && typeof g === "object") {
        return pickString((g as Record<string, unknown>).tag) || null
      }
      return pickString(row.guildTag ?? row.guild_tag) || null
    })(),
    guildName: (() => {
      const g = row.guild
      if (g && typeof g === "object") {
        return pickString((g as Record<string, unknown>).name) || null
      }
      return pickString(row.guildName ?? row.guild_name) || null
    })(),
    guildEmblemKey: (() => {
      const g = row.guild
      if (g && typeof g === "object") {
        return (
          pickString(
            (g as Record<string, unknown>).emblemKey ??
              (g as Record<string, unknown>).emblem_key
          ) || null
        )
      }
      return (
        pickString(row.guildEmblemKey ?? row.guild_emblem_key) || null
      )
    })(),
    pet: normalizePetPublicBrief(row.pet ?? row.playerPet ?? row.player_pet),
    lastActivityAt: pickIsoDate(
      row.lastActivityAt ?? row.last_activity_at
    ),
  }
}

function normalizeLeaderboard(
  data: unknown,
  options: { scope?: LeaderboardScope; type?: LeaderboardType } = {}
): LeaderboardResponse {
  const scope = options.scope ?? "city"
  const type = options.type ?? "personal"
  const obj = unwrapGamePayload(data)
  const rawEntries = Array.isArray(obj.entries)
    ? obj.entries
    : Array.isArray(obj.leaderboard)
      ? obj.leaderboard
      : Array.isArray(obj.items)
        ? obj.items
        : Array.isArray(data)
          ? data
          : []

  const entries = rawEntries
    .map((entry, index) =>
      entry && typeof entry === "object"
        ? normalizeLeaderboardEntry(entry as Record<string, unknown>, index, scope)
        : null
    )
    .filter((entry): entry is LeaderboardEntry => entry != null)

  const myEntry =
    obj.myEntry && typeof obj.myEntry === "object"
      ? (obj.myEntry as Record<string, unknown>)
      : null
  const viewer =
    obj.viewer && typeof obj.viewer === "object" && !Array.isArray(obj.viewer)
      ? (obj.viewer as Record<string, unknown>)
      : null

  const myRankRaw =
    viewer?.rank ?? obj.myRank ?? obj.my_rank ?? obj.viewerRank ?? obj.viewer_rank ?? myEntry?.rank
  const myPtsFromFields =
    viewer?.pts ??
    viewer?.points ??
    (scope === "global"
      ? viewer?.globalPts ?? viewer?.global_pts ?? obj.myGlobalPts ?? obj.my_global_pts
      : viewer?.localPts ?? viewer?.local_pts ?? obj.myLocalPts ?? obj.my_local_pts) ??
    obj.myPts ??
    obj.my_pts ??
    myEntry?.pts ??
    myEntry?.points ??
    (scope === "global"
      ? myEntry?.globalPts ?? myEntry?.global_pts
      : myEntry?.localPts ?? myEntry?.local_pts)
  const meFromList = entries.find((entry) => entry.isMe)
  const myPtsRaw = myPtsFromFields ?? meFromList?.pts
  const myRankResolved =
    myRankRaw != null && myRankRaw !== ""
      ? pickNumber(myRankRaw, NaN) || null
      : meFromList?.rank ?? null
  const myPtsResolved =
    myPtsRaw == null || myPtsRaw === ""
      ? meFromList?.pts ?? null
      : pickNumber(myPtsRaw, NaN) || null
  const myGuardianRank =
    pickGuardianRankFromRecord(viewer) ??
    pickGuardianRankFromRecord(myEntry) ??
    pickGuardianRankFromRecord(obj)
  const leagueRaw = pickString(obj.league ?? obj.academicLeague).toUpperCase()

  return {
    type: (pickString(obj.type, type) as LeaderboardType) || type,
    scope: (pickString(obj.scope, scope) as LeaderboardScope) || scope,
    league:
      leagueRaw === "TITAN" || leagueRaw === "ATLANT"
        ? (leagueRaw as AcademicLeague)
        : null,
    weekStart: pickString(obj.weekStart ?? obj.week_start) || null,
    cityId: pickNumber(obj.cityId ?? obj.city_id) || null,
    cityName: pickString(obj.cityName ?? obj.city_name) || null,
    entries,
    myRank: myRankResolved,
    myPts: myPtsResolved,
    myGuardianRank: myGuardianRank ?? null,
    warStatus: normalizeWarStatus(obj.warStatus ?? obj.war_status ?? obj),
  }
}

function normalizeShopItems(data: unknown): ShopItemsResponse {
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const rawItems = Array.isArray(obj.items)
    ? obj.items
    : Array.isArray(data)
      ? data
      : []
  const items = rawItems
    .map((item) =>
      item && typeof item === "object"
        ? normalizeShopItem(item as Record<string, unknown>)
        : null
    )
    .filter((item): item is ShopItem => item != null)
    .filter((item) => item.isActive !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))

  return { items }
}

function normalizeQuizQuestion(raw: unknown): QuizQuestion | null {
  if (!raw || typeof raw !== "object") return null
  const obj = raw as Record<string, unknown>
  const id = obj.id ?? obj.questionId ?? obj.question_id
  if (id == null) return null

  const question = pickString(obj.question ?? obj.text ?? obj.title)
  const optionsRaw = obj.options ?? obj.choices ?? obj.answers
  const options = Array.isArray(optionsRaw)
    ? optionsRaw
        .map((option) => {
          if (typeof option === "string") return option
          if (option && typeof option === "object") {
            return pickString(
              (option as Record<string, unknown>).text ??
                (option as Record<string, unknown>).label ??
                (option as Record<string, unknown>).value
            )
          }
          return ""
        })
        .filter(Boolean)
    : []

  if (!question || options.length === 0) return null

  const category = normalizeQuizCategory(
    obj.category ?? obj.categoryCode ?? obj.category_code
  )

  return {
    id: String(id),
    question,
    options,
    ...(category ? { category } : {}),
  }
}

function extractQuizQuestions(data: unknown): unknown[] {
  if (Array.isArray(data)) return data
  const obj = unwrapGamePayload(data)
  if (Array.isArray(obj)) return obj
  if (Array.isArray(obj.questions)) return obj.questions
  if (Array.isArray(obj.items)) return obj.items
  return []
}

function normalizeDailyQuiz(data: unknown): DailyQuizResponse {
  const obj = unwrapGamePayload(data)
  const questions = extractQuizQuestions(data)
    .map(normalizeQuizQuestion)
    .filter((question): question is QuizQuestion => question != null)

  const cooldownUntil =
    pickString(obj.cooldownUntil ?? obj.cooldown_until) || null

  return { questions, cooldownUntil }
}

function buildQuizAnswersPayload(
  questions: QuizQuestion[],
  answers: Record<string, string>
) {
  return {
    answers: questions.map((question) => ({
      questionId: question.id,
      answerIndex: Math.max(0, question.options.indexOf(answers[question.id] ?? "")),
    })),
  }
}

function normalizeVerifyQuizResponse(data: unknown): VerifyQuizResponse {
  const obj = unwrapGamePayload(data)
  const correctCount = pickNumber(
    obj.correct ?? obj.correctCount ?? obj.correct_count
  )
  const totalCount = pickNumber(
    obj.total ?? obj.totalCount ?? obj.total_count,
    3
  )
  const rewardCharges = pickNumber(
    obj.rewardCharges ?? obj.reward_charges ?? obj.rewardChakra ?? obj.reward_chakra
  )

  return {
    passport:
      obj.passport && typeof obj.passport === "object"
        ? (obj.passport as DigitalPassport)
        : undefined,
    correctCount,
    totalCount,
    rewardGranted:
      rewardCharges > 0 ||
      pickBool(obj.rewardGranted ?? obj.reward_granted ?? obj.passed),
    rewardCharges,
    perfectQuizCoins: pickNumber(
      obj.perfectQuizCoins ?? obj.perfect_quiz_coins
    ) || undefined,
  }
}

function normalizeRankExam(data: unknown): RankExamResponse {
  const obj = unwrapGamePayload(data)
  const questions = extractQuizQuestions(data)
    .map(normalizeQuizQuestion)
    .filter((question): question is QuizQuestion => question != null)

  const blockedUntil =
    pickString(obj.blockedUntil ?? obj.blocked_until) || null

  return { questions, blockedUntil }
}

function normalizeRankExamSubmit(
  data: unknown,
  cardId: string
): RankExamSubmitResponse {
  const obj = unwrapGamePayload(data)
  const { passport: parsedPassport, card: parsedCard } = normalizeCardActionResponse(
    data,
    { preferredCardId: cardId }
  )

  let card = parsedCard
  if (!card) {
    card =
      normalizeGameCard(
        obj.card ??
          obj.updatedCard ??
          obj.updated_card ??
          obj.resultCard ??
          obj.result_card
      ) ?? null
  }

  if (!card) {
    const cards = normalizeGameCards(obj)
    card = cards.find((c) => c.id === cardId) ?? null
  }

  const passport =
    parsedPassport ??
    normalizeDigitalPassport(
      obj.passport ??
        obj.profile ??
        obj.digitalPassport ??
        obj.digital_passport
    )

  return {
    passport,
    card,
    passed: pickBool(obj.passed ?? obj.success),
    correctCount: pickNumber(
      obj.correctCount ?? obj.correct_count ?? obj.correct
    ),
    totalCount: pickNumber(
      obj.totalCount ?? obj.total_count ?? obj.total,
      RANK_EXAM_TOTAL
    ),
    crystallized: normalizeCrystallized(obj.crystallized),
  }
}

export const getDailyQuiz = async (): Promise<DailyQuizResponse> => {
  const { data } = await api.get<unknown>("/game/academy/daily-quiz")
  return normalizeDailyQuiz(data)
}

export const verifyDailyQuiz = async (
  questions: QuizQuestion[],
  answers: Record<string, string>
): Promise<VerifyQuizResponse> => {
  const { data } = await api.post<unknown>(
    "/game/academy/verify-quiz",
    buildQuizAnswersPayload(questions, answers)
  )
  return normalizeVerifyQuizResponse(data)
}

export const getRankExam = async (cardId: string): Promise<RankExamResponse> => {
  const { data } = await api.get<unknown>(`/game/academy/rank-exam/${cardId}`)
  return normalizeRankExam(data)
}

export const submitRankExam = async (
  cardId: string,
  questions: QuizQuestion[],
  answers: Record<string, string>
): Promise<RankExamSubmitResponse> => {
  const { data } = await api.post<unknown>("/game/academy/rank-exam", {
    cardId,
    ...buildQuizAnswersPayload(questions, answers),
  })
  const result = normalizeRankExamSubmit(data, cardId)
  if (result.passed) {
    handleAlmanacStampsFromResponse(data)
  }
  return result
}

export const getOnboarding = async (): Promise<OnboardingResponse> => {
  const { data } = await api.get<unknown>("/game/onboarding")
  return normalizeOnboarding(data)
}

export const getDailyQuests = async (): Promise<DailyQuestsResponse> => {
  const { data } = await api.get<unknown>("/game/quests/daily")
  return normalizeDailyQuests(data)
}

export const claimDailyQuest = async (
  questKey: DailyQuestKey
): Promise<ClaimDailyQuestResponse> => {
  const { data } = await api.post<unknown>("/game/quests/daily/claim", {
    questKey,
    quest_key: questKey,
  })
  return normalizeClaimDailyQuestResponse(data, questKey)
}

export const getWeeklyQuests = async (): Promise<WeeklyQuestsResponse> => {
  const { data } = await api.get<unknown>("/game/quests/weekly")
  return normalizeWeeklyQuests(data)
}

export const claimWeeklyQuest = async (
  questKey: WeeklyQuestKey
): Promise<ClaimWeeklyQuestResponse> => {
  const { data } = await api.post<unknown>("/game/quests/weekly/claim", {
    questKey,
    quest_key: questKey,
  })
  return normalizeClaimWeeklyQuestResponse(data, questKey)
}

export const getBattleHistory = async (
  limit = 20
): Promise<BattleHistoryEntry[]> => {
  const { data } = await api.get<unknown>("/game/passport/battle-history", {
    params: { limit },
  })
  return normalizeBattleHistory(data)
}

export const getLeaderboard = async (
  scope: LeaderboardScope = "city",
  limit = 50
): Promise<LeaderboardResponse> => {
  const { data } = await api.get<unknown>("/game/leaderboard", {
    params: { type: "personal", scope, limit },
  })
  return normalizeLeaderboard(data, { scope, type: "personal" })
}

export const getLeagueLeaderboard = async (
  league: AcademicLeague,
  limit = 50
): Promise<LeaderboardResponse> => {
  const { data } = await api.get<unknown>("/game/leaderboard", {
    params: { type: "league", league, limit },
  })
  return normalizeLeaderboard(data, { type: "league" })
}

export const getCityWarLeaderboard = async (): Promise<LeaderboardResponse> => {
  const { data } = await api.get<unknown>("/game/leaderboard", {
    params: { type: "city_war" },
  })
  return normalizeLeaderboard(data, { type: "city_war" })
}

export const getLeagueBalance = async (): Promise<LeagueBalance | null> => {
  try {
    const { data } = await api.get<unknown>("/game/academy/league-balance")
    return normalizeLeagueBalance(unwrapGamePayload(data))
  } catch {
    try {
      const { data } = await api.get<unknown>("/game/faction/league-balance")
      return normalizeLeagueBalance(unwrapGamePayload(data))
    } catch (error) {
      guardiansLogApiError("GET league-balance", error)
      throw error
    }
  }
}

export const getGuardianPaths = async (): Promise<GuardianPathInfo[]> => {
  const endpoints = ["/game/guardian-paths", "/game/academy/guardian-paths"]
  let lastError: unknown = null

  for (const url of endpoints) {
    try {
      const { data } = await api.get<unknown>(url)
      const list = normalizeGuardianPathList(data)
      if (list.length) return list
      const wrapped = normalizeGuardianPathList(unwrapGamePayload(data))
      if (wrapped.length) return wrapped
    } catch (error) {
      lastError = error
      if (axios.isAxiosError(error) && error.response?.status === 404) continue
      throw error
    }
  }

  if (lastError) throw lastError
  return normalizeGuardianPathList([])
}

export const getGameMechanics = async (): Promise<GameMechanics> => {
  const { data } = await api.get<unknown>("/game/mechanics")
  const mechanics = normalizeGameMechanics(data)
  if (!mechanics) {
    throw new Error("Не удалось загрузить механику игры")
  }
  return mechanics
}

function isGuardianPathRouteMissing(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  if (error.response?.status !== 404) return false
  const data = error.response.data
  if (typeof data === "string") {
    return data.includes("Cannot POST") || data.includes("Cannot PATCH")
  }
  return false
}

async function postGuardianPathSelection(
  url: string,
  guardianPath: GuardianPath
): Promise<DigitalPassport | null> {
  const { data } = await api.post<unknown>(url, { guardianPath })
  const passport =
    normalizeDigitalPassport(data) ??
    normalizeDigitalPassport(unwrapGamePayload(data))
  return passport
}

export const selectGuardianPath = async (
  guardianPath: GuardianPath
): Promise<DigitalPassport> => {
  const endpoints = [
    "/game/passport/guardian-path",
    "/game/academy/select-guardian-path",
  ]

  let lastError: unknown = null
  for (const url of endpoints) {
    try {
      const passport = await postGuardianPathSelection(url, guardianPath)
      if (passport) return passport
      throw new Error("Не удалось сохранить путь хранителя")
    } catch (error) {
      lastError = error
      if (isGuardianPathRouteMissing(error)) continue
      throw error
    }
  }

  if (lastError) throw lastError
  throw new Error("Не удалось сохранить путь хранителя")
}

export const getWarStatus = async (): Promise<WarStatus | null> => {
  const { data } = await api.get<unknown>("/game/faction/war-status")
  return normalizeWarStatus(unwrapGamePayload(data))
}

export const claimWarMilestone = async (
  threshold: number
): Promise<{ passport?: DigitalPassport; warStatus?: WarStatus | null }> => {
  const { data } = await api.post<unknown>("/game/faction/claim-milestone", {
    threshold,
  })
  const obj = unwrapGamePayload(data)
  return {
    passport: normalizeDigitalPassport(obj.passport ?? obj.profile) ?? undefined,
    warStatus: normalizeWarStatus(obj.warStatus ?? obj.war_status ?? obj),
  }
}

export const claimTopWarReward = async (
  choice: TopRewardChoice
): Promise<{ passport?: DigitalPassport; warStatus?: WarStatus | null }> => {
  const { data } = await api.post<unknown>("/game/faction/claim-top-reward", {
    choice,
  })
  const obj = unwrapGamePayload(data)
  return {
    passport: normalizeDigitalPassport(obj.passport ?? obj.profile) ?? undefined,
    warStatus: normalizeWarStatus(obj.warStatus ?? obj.war_status ?? obj),
  }
}

export const claimCounterattack = async (): Promise<{
  passport?: DigitalPassport
  warStatus?: WarStatus | null
}> => {
  const { data } = await api.post<unknown>("/game/faction/claim-counterattack")
  const obj = unwrapGamePayload(data)
  return {
    passport: normalizeDigitalPassport(obj.passport ?? obj.profile) ?? undefined,
    warStatus: normalizeWarStatus(obj.warStatus ?? obj.war_status ?? obj),
  }
}

export const claimDominanceReward = async (
  choice: DominanceRewardChoice
): Promise<{ passport?: DigitalPassport; warStatus?: WarStatus | null }> => {
  const { data } = await api.post<unknown>(
    "/game/faction/claim-dominance-reward",
    { choice }
  )
  const obj = unwrapGamePayload(data)
  return {
    passport: normalizeDigitalPassport(obj.passport ?? obj.profile) ?? undefined,
    warStatus: normalizeWarStatus(obj.warStatus ?? obj.war_status ?? obj),
  }
}

export const getFactionTournaments = async (): Promise<FactionTournament[]> => {
  const { data } = await api.get<unknown>("/game/faction/tournaments")
  const obj = unwrapGamePayload(data)
  const raw = Array.isArray(obj)
    ? obj
    : Array.isArray(obj.tournaments)
      ? obj.tournaments
      : Array.isArray(obj.items)
        ? obj.items
        : []
  return raw
    .map(normalizeFactionTournament)
    .filter((item): item is FactionTournament => item != null)
}

export const getLeagueFinalCandidates = async (
  league: AcademicLeague
): Promise<LeagueFinalCandidate[]> => {
  const { data } = await api.get<unknown>(
    "/game/faction/league-final-candidates",
    { params: { league } }
  )
  const obj = unwrapGamePayload(data)
  const raw = Array.isArray(obj)
    ? obj
    : Array.isArray(obj.candidates)
      ? obj.candidates
      : Array.isArray(obj.entries)
        ? obj.entries
        : []
  return raw
    .map(normalizeLeagueFinalCandidate)
    .filter((item): item is LeagueFinalCandidate => item != null)
}

export const getCityFinalCandidates = async (): Promise<{
  titan?: LeagueFinalCandidate | null
  atlant?: LeagueFinalCandidate | null
}> => {
  const { data } = await api.get<unknown>("/game/faction/city-final-candidates")
  const obj = unwrapGamePayload(data)
  return {
    titan: normalizeLeagueFinalCandidate(obj.titan ?? obj.titanCandidate),
    atlant: normalizeLeagueFinalCandidate(obj.atlant ?? obj.atlantCandidate),
  }
}

export const getShopItems = async (): Promise<ShopItemsResponse> => {
  const { data } = await api.get<unknown>("/game/shop/items")
  return normalizeShopItems(data)
}

export const purchaseShopItem = async (
  itemId: string
): Promise<ShopPurchaseResponse> => {
  const { data } = await api.post<unknown>("/game/shop/purchase", {
    itemId,
    item_id: itemId,
  })
  const obj = unwrapGamePayload(data)
  const passport =
    normalizeDigitalPassport(obj.passport ?? obj.profile ?? obj) ?? null
  if (!passport) {
    throw new Error("Не удалось обновить паспорт после покупки")
  }
  return {
    passport,
    itemId: pickString(obj.itemId ?? obj.item_id, itemId),
  }
}

export const equipProfileCosmetic = async (body: {
  kind: "bg" | "nick" | "pulse"
  key: string | null
}): Promise<DigitalPassport> => {
  const { data } = await api.post<unknown>("/game/passport/cosmetics/equip", body)
  const passport = normalizeDigitalPassport(unwrapGamePayload(data))
  if (!passport) {
    throw new Error("Не удалось обновить косметику")
  }
  return passport
}

function normalizeBattleOutcome(value: unknown): BattleOutcome {
  const raw = pickString(value).toUpperCase()
  if (raw === "WIN" || raw === "LOSE" || raw === "DRAW") return raw
  return "DRAW"
}

function normalizeBattleCardBreakdown(raw: unknown): BattleCardBreakdown {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const phaseRaw = pickString(
    obj.guardianPassivePhase ?? obj.guardian_passive_phase
  ).toLowerCase()
  const guardianPassivePhase =
    phaseRaw === "early" || phaseRaw === "late" ? phaseRaw : null
  return {
    baseBS: pickNumber(obj.baseBS ?? obj.base_bs),
    schoolCoefficient: pickNumber(
      obj.schoolCoefficient ?? obj.school_coefficient,
      1
    ),
    rebornMultiplier: pickNumber(obj.rebornMultiplier ?? obj.reborn_multiplier, 1),
    elementBonus: pickNumber(obj.elementBonus ?? obj.element_bonus),
    styleBonus: pickNumber(obj.styleBonus ?? obj.style_bonus),
    shardBonus: pickNumber(obj.shardBonus ?? obj.shard_bonus),
    guardianPassiveBonus: pickNumber(
      obj.guardianPassiveBonus ?? obj.guardian_passive_bonus
    ),
    guardianPassivePhase,
    guardianPassiveLabel:
      pickString(obj.guardianPassiveLabel ?? obj.guardian_passive_label) || null,
    petBonus: pickNumber(obj.petBonus ?? obj.pet_bonus),
    petLabel: pickString(obj.petLabel ?? obj.pet_label) || null,
    petLevel: (() => {
      const raw = obj.petLevel ?? obj.pet_level
      if (raw == null || raw === "") return null
      const n = Math.floor(pickNumber(raw, 0))
      return n > 0 ? n : null
    })(),
    petCritTriggered: pickBool(
      obj.petCritTriggered ?? obj.pet_crit_triggered
    ),
    petDodgeTriggered: pickBool(
      obj.petDodgeTriggered ?? obj.pet_dodge_triggered
    ),
    guardianCritTriggered: pickBool(
      obj.guardianCritTriggered ?? obj.guardian_crit_triggered
    ),
    guardianDodgeTriggered: pickBool(
      obj.guardianDodgeTriggered ?? obj.guardian_dodge_triggered
    ),
    shardCritTriggered: pickBool(
      obj.shardCritTriggered ?? obj.shard_crit_triggered
    ),
    shardDodgeTriggered: pickBool(
      obj.shardDodgeTriggered ?? obj.shard_dodge_triggered
    ),
    shardPtsLossReduction: pickNumber(
      obj.shardPtsLossReduction ?? obj.shard_pts_loss_reduction
    ),
    iceAntiElementPenalty: pickNumber(
      obj.iceAntiElementPenalty ??
        obj.ice_anti_element_penalty ??
        obj.iceAntiStylePenalty ??
        obj.ice_anti_style_penalty
    ),
    abilityId: pickString(obj.abilityId ?? obj.ability_id) || null,
    abilitySlot: (() => {
      const slot = pickNumber(obj.abilitySlot ?? obj.ability_slot, 0)
      return slot === 1 || slot === 2 || slot === 3 ? slot : null
    })(),
    abilityTitle: pickString(obj.abilityTitle ?? obj.ability_title) || null,
    abilityBonus: pickNumber(obj.abilityBonus ?? obj.ability_bonus),
    abilityDebuffDealt: pickNumber(
      obj.abilityDebuffDealt ?? obj.ability_debuff_dealt
    ),
    abilitySilenced: pickBool(obj.abilitySilenced ?? obj.ability_silenced),
    effectiveBS: pickNumber(obj.effectiveBS ?? obj.effective_bs),
  }
}

function normalizeTrainingStatus(raw: unknown): TrainingStatus {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  return {
    trainingBattlesToday: pickNumber(
      obj.trainingBattlesToday ?? obj.training_battles_today
    ),
    dailyRewardLimit: pickNumber(
      obj.dailyRewardLimit ?? obj.daily_reward_limit,
      20
    ),
    remainingRewardedBattles: pickNumber(
      obj.remainingRewardedBattles ?? obj.remaining_rewarded_battles
    ),
    coinsPerWin: pickNumber(obj.coinsPerWin ?? obj.coins_per_win, 50),
  }
}

function normalizeTrainingFightResult(raw: unknown): TrainingFightResult {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const combatRaw =
    obj.combat && typeof obj.combat === "object" && !Array.isArray(obj.combat)
      ? (obj.combat as Record<string, unknown>)
      : {}
  const yourCard = normalizeBattleCardView(obj.yourCard ?? obj.your_card)
  const shadowCard = normalizeBattleCardView(obj.shadowCard ?? obj.shadow_card)
  if (!yourCard || !shadowCard) {
    throw new Error("Некорректный ответ тренировки")
  }
  return {
    battleId: pickString(obj.battleId ?? obj.battle_id, "training"),
    mode: "TRAINING",
    shadowName: pickString(obj.shadowName ?? obj.shadow_name, "Тень соперника"),
    outcome: normalizeBattleOutcome(obj.outcome),
    yourCard,
    shadowCard,
    combat: {
      player: normalizeBattleCardBreakdown(combatRaw.player),
      opponent: normalizeBattleCardBreakdown(combatRaw.opponent),
    },
    coinsDelta: pickNumber(obj.coinsDelta ?? obj.coins_delta),
    rewardsApplied: pickBool(obj.rewardsApplied ?? obj.rewards_applied),
    trainingBattlesToday: pickNumber(
      obj.trainingBattlesToday ?? obj.training_battles_today
    ),
    dailyRewardLimit: pickNumber(
      obj.dailyRewardLimit ?? obj.daily_reward_limit,
      20
    ),
    remainingRewardedBattles: pickNumber(
      obj.remainingRewardedBattles ?? obj.remaining_rewarded_battles
    ),
  }
}

export const getTrainingStatus = async (): Promise<TrainingStatus> => {
  const { data } = await api.get<unknown>("/game/training/status")
  return normalizeTrainingStatus(data)
}

export const startTrainingFight = async (
  cardId: string,
  abilityId?: string | null
): Promise<TrainingFightResult> => {
  const { data } = await api.post<unknown>("/game/training/fight", {
    cardId,
    ...(abilityId ? { abilityId } : {}),
  })
  return normalizeTrainingFightResult(data)
}

function normalizeBossFightResult(raw: unknown): BossFightResult {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const combatRaw =
    obj.combat && typeof obj.combat === "object" && !Array.isArray(obj.combat)
      ? (obj.combat as Record<string, unknown>)
      : {}
  const yourCard = normalizeBattleCardView(obj.yourCard ?? obj.your_card)
  const bossCard = normalizeBattleCardView(obj.bossCard ?? obj.boss_card)
  if (!yourCard || !bossCard) {
    throw new Error("Некорректный ответ боя с боссом")
  }
  return {
    battleId: pickString(obj.battleId ?? obj.battle_id, "boss"),
    mode: "BOSS",
    bossName: pickString(obj.bossName ?? obj.boss_name, "Страж"),
    bossPointId: pickString(obj.bossPointId ?? obj.boss_point_id),
    outcome: normalizeBattleOutcome(obj.outcome),
    yourCard,
    bossCard,
    combat: {
      player: normalizeBattleCardBreakdown(combatRaw.player),
      opponent: normalizeBattleCardBreakdown(combatRaw.opponent),
    },
    rewardCards: normalizeGameCards(obj.rewardCards ?? obj.reward_cards),
    rewardsApplied: pickBool(obj.rewardsApplied ?? obj.rewards_applied),
    cooldownUntil: pickString(obj.cooldownUntil ?? obj.cooldown_until),
    collectionFull:
      obj.collectionFull === true ||
      obj.collection_full === true ||
      undefined,
  }
}

export const getBossPoints = async (): Promise<BossPointView[]> => {
  const { data } = await api.get<unknown>("/game/boss/points")
  const raw = extractBossPointsPayload(data)
  const points = mapBossPoints(data)
  if (__DEV__ && raw.length > 0 && points.length === 0) {
    console.warn(
      "[boss/points] API вернул боссов, но парсер их отбросил. Пример:",
      raw[0]
    )
  }
  if (__DEV__) {
    console.log(`[boss/points] raw=${raw.length} parsed=${points.length}`)
  }
  return points
}

export const startBossFight = async (
  body: BossFightRequest
): Promise<BossFightResult> => {
  const { data } = await api.post<unknown>("/game/boss/fight", body)
  const result = normalizeBossFightResult(data)
  handleAlmanacStampsFromResponse(data)
  return result
}

function normalizeWorldBossStatus(value: unknown): WorldBossStatus {
  if (value === "DEFEATED" || value === "EXPIRED" || value === "ACTIVE") {
    return value
  }
  return "ACTIVE"
}

function normalizeWorldBossEvent(raw: unknown): WorldBossEventView | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const id = pickString(obj.id)
  if (!id) return null
  const bossCard = normalizeBattleCardView(obj.bossCard ?? obj.boss_card)
  if (!bossCard) return null
  const poolRaw =
    obj.rewardPool && typeof obj.rewardPool === "object" && !Array.isArray(obj.rewardPool)
      ? (obj.rewardPool as Record<string, unknown>)
      : obj.reward_pool && typeof obj.reward_pool === "object" && !Array.isArray(obj.reward_pool)
        ? (obj.reward_pool as Record<string, unknown>)
        : {}
  const usedRaw = obj.usedCardIds ?? obj.used_card_ids
  const usedCardIds = Array.isArray(usedRaw)
    ? usedRaw.map((id) => String(id)).filter(Boolean)
    : []
  const boardRaw = obj.leaderboard
  const leaderboard = Array.isArray(boardRaw)
    ? boardRaw
        .map((row) => {
          if (!row || typeof row !== "object") return null
          const item = row as Record<string, unknown>
          const userId = Number(item.userId ?? item.user_id)
          if (!Number.isFinite(userId)) return null
          return {
            userId,
            nickname: pickString(item.nickname, "Хранитель"),
            damage: Math.max(0, Math.floor(Number(item.damage) || 0)),
            sharePercent: Number(item.sharePercent ?? item.share_percent) || 0,
          }
        })
        .filter((row): row is NonNullable<typeof row> => row != null)
    : []

  const element = pickString(obj.element, "FIRE") as WorldBossEventView["element"]
  const style = pickString(obj.style, "ATTACK") as WorldBossEventView["style"]
  const dustElement = pickString(
    poolRaw.dustElement ?? poolRaw.dust_element,
    element
  ) as WorldBossEventView["element"]

  return {
    id,
    cityId: Math.floor(Number(obj.cityId ?? obj.city_id) || 0),
    eventDate: pickString(obj.eventDate ?? obj.event_date),
    name: pickString(obj.name, "Мировой босс"),
    element,
    style,
    maxHp: Math.max(0, Math.floor(Number(obj.maxHp ?? obj.max_hp) || 0)),
    currentHp: Math.max(0, Math.floor(Number(obj.currentHp ?? obj.current_hp) || 0)),
    status: normalizeWorldBossStatus(obj.status),
    latitude: Number(obj.latitude) || 0,
    longitude: Number(obj.longitude) || 0,
    radiusMeters: Math.max(
      1,
      Math.floor(Number(obj.radiusMeters ?? obj.radius_meters) || 80)
    ),
    startsAt: pickString(obj.startsAt ?? obj.starts_at),
    endsAt: pickString(obj.endsAt ?? obj.ends_at),
    defeatedAt: pickString(obj.defeatedAt ?? obj.defeated_at) || null,
    rewardPool: {
      coins: Math.max(0, Math.floor(Number(poolRaw.coins) || 0)),
      chakra: Math.max(0, Math.floor(Number(poolRaw.chakra) || 0)),
      dust: Math.max(0, Math.floor(Number(poolRaw.dust) || 0)),
      dustElement,
    },
    energyCostInRange: Math.max(
      0,
      Math.floor(
        Number(obj.energyCostInRange ?? obj.energy_cost_in_range) || 0
      )
    ),
    energyCostRemote: Math.max(
      0,
      Math.floor(
        Number(
          obj.energyCostRemote ??
            obj.energy_cost_remote ??
            obj.energyCost ??
            obj.energy_cost
        ) || 5
      )
    ),
    energyCost: Math.max(
      0,
      Math.floor(
        Number(
          obj.energyCostRemote ??
            obj.energy_cost_remote ??
            obj.energyCost ??
            obj.energy_cost
        ) || 5
      )
    ),
    myDamage: Math.max(0, Math.floor(Number(obj.myDamage ?? obj.my_damage) || 0)),
    usedCardIds,
    mySharePercent:
      obj.mySharePercent == null && obj.my_share_percent == null
        ? null
        : Number(obj.mySharePercent ?? obj.my_share_percent),
    leaderboard,
    bossCard,
  }
}

function normalizeWorldBossAttackResult(raw: unknown): WorldBossAttackResult {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const combatRaw =
    obj.combat && typeof obj.combat === "object" && !Array.isArray(obj.combat)
      ? (obj.combat as Record<string, unknown>)
      : {}
  const yourCard = normalizeBattleCardView(obj.yourCard ?? obj.your_card)
  const bossCard = normalizeBattleCardView(obj.bossCard ?? obj.boss_card)
  if (!yourCard || !bossCard) {
    throw new Error("Некорректный ответ атаки мирового босса")
  }
  const rewardsRaw =
    obj.rewards && typeof obj.rewards === "object" && !Array.isArray(obj.rewards)
      ? (obj.rewards as Record<string, unknown>)
      : null
  const usedRaw = obj.usedCardIds ?? obj.used_card_ids
  return {
    eventId: pickString(obj.eventId ?? obj.event_id),
    mode: "WORLD_BOSS",
    bossName: pickString(obj.bossName ?? obj.boss_name, "Мировой босс"),
    outcome: normalizeBattleOutcome(obj.outcome),
    yourCard,
    bossCard,
    combat: {
      player: normalizeBattleCardBreakdown(combatRaw.player),
      opponent: normalizeBattleCardBreakdown(combatRaw.opponent),
    },
    rawDamage: Math.max(0, Math.floor(Number(obj.rawDamage ?? obj.raw_damage) || 0)),
    effectiveDamage: Math.max(
      0,
      Math.floor(Number(obj.effectiveDamage ?? obj.effective_damage) || 0)
    ),
    bossHpBefore: Math.max(
      0,
      Math.floor(Number(obj.bossHpBefore ?? obj.boss_hp_before) || 0)
    ),
    bossHpAfter: Math.max(
      0,
      Math.floor(Number(obj.bossHpAfter ?? obj.boss_hp_after) || 0)
    ),
    bossDefeated: pickBool(obj.bossDefeated ?? obj.boss_defeated),
    energySpent: Math.max(
      0,
      Math.floor(Number(obj.energySpent ?? obj.energy_spent) || 0)
    ),
    energyLeft: Math.max(
      0,
      Math.floor(Number(obj.energyLeft ?? obj.energy_left) || 0)
    ),
    joinedRemote: pickBool(obj.joinedRemote ?? obj.joined_remote, true),
    rewards: rewardsRaw
      ? {
          coins: Math.max(0, Math.floor(Number(rewardsRaw.coins) || 0)),
          chakra: Math.max(0, Math.floor(Number(rewardsRaw.chakra) || 0)),
          dust: Math.max(0, Math.floor(Number(rewardsRaw.dust) || 0)),
          dustElement: pickString(
            rewardsRaw.dustElement ?? rewardsRaw.dust_element,
            "FIRE"
          ) as import("../types/battle").CardElement,
        }
      : null,
    myTotalDamage: Math.max(
      0,
      Math.floor(Number(obj.myTotalDamage ?? obj.my_total_damage) || 0)
    ),
    usedCardIds: Array.isArray(usedRaw)
      ? usedRaw.map((id) => String(id)).filter(Boolean)
      : [],
  }
}

export const getWorldBossActive = async (): Promise<WorldBossActiveResponse> => {
  const { data } = await api.get<unknown>("/game/world-boss/active")
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const reason =
    obj.reason === "NO_SPAWN_POINT" ? ("NO_SPAWN_POINT" as const) : null
  return {
    event: normalizeWorldBossEvent(obj.event),
    reason,
  }
}

export const attackWorldBoss = async (
  body: WorldBossAttackRequest
): Promise<WorldBossAttackResult> => {
  const { data } = await api.post<unknown>("/game/world-boss/attack", body)
  return normalizeWorldBossAttackResult(data)
}

export const getCardShopPoints = async (): Promise<CardShopPoint[]> => {
  const { data } = await api.get<unknown>("/game/card-shop/points")
  const points = mapCardShopPoints(data)
  if (__DEV__) {
    console.log(`[card-shop/points] parsed=${points.length}`)
  }
  return points
}

export const getBoosterShopPoints = async (): Promise<BoosterShopPoint[]> => {
  const { data } = await api.get<unknown>("/game/booster-shop/points")
  const points = mapBoosterShopPoints(data)
  if (__DEV__) {
    console.log(`[booster-shop/points] parsed=${points.length}`)
  }
  return points
}

export const getCardShopCatalog = async (params: {
  pointId: string
  playerLat: number
  playerLng: number
}): Promise<CardShopCatalog> => {
  const pointId = String(params.pointId ?? "").trim()
  const playerLat = Number(params.playerLat)
  const playerLng = Number(params.playerLng)
  if (!pointId || !Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS для загрузки каталога")
  }

  const { data } = await api.get<unknown>("/game/card-shop/catalog", {
    params: {
      pointId,
      playerLat: Number(playerLat.toFixed(6)),
      playerLng: Number(playerLng.toFixed(6)),
    },
  })
  const catalog = normalizeCardShopCatalog(data)
  if (!catalog) {
    throw new Error("Не удалось загрузить каталог лавки")
  }
  return catalog
}

export const purchaseCardShopOffer = async (
  body: CardShopPurchaseBody
): Promise<CardShopPurchaseResponse> => {
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  const { data } = await api.post<unknown>("/game/card-shop/purchase", {
    pointId: String(body.pointId),
    offerId: String(body.offerId),
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
  })
  const result = normalizeCardShopPurchaseResponse(data)
  if (!result) {
    throw new Error("Не удалось оформить покупку")
  }
  handleAlmanacStampsFromResponse(data)
  return result
}

function normalizeMailGeoBody(body: MailGeoBody): MailGeoBody {
  const pointId = String(body.pointId ?? "").trim()
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  if (!pointId || !Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS и точка почты")
  }
  return {
    pointId,
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
  }
}

export const getPostOfficePoints = async (): Promise<PostOfficePoint[]> => {
  const { data } = await api.get<unknown>("/game/mail/post-offices/points")
  const points = mapPostOfficePoints(data)
  if (__DEV__) {
    console.log(`[mail/post-offices/points] parsed=${points.length}`)
  }
  return points
}

export const getTreasurePoints = async (): Promise<TreasurePointsResponse> => {
  const { data } = await api.get<unknown>("/game/treasure/points")
  const result = normalizeTreasurePointsResponse(data)
  if (__DEV__) {
    console.log(
      `[treasure/points] enabled=${result.isEnabled} points=${result.points.length} claimedToday=${result.claimedToday}`
    )
  }
  return result
}

export const claimTreasure = async (
  body: TreasureClaimBody
): Promise<TreasureClaimResponse> => {
  const pointId = String(body.pointId ?? "").trim()
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  if (!pointId || !Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS и точка клада")
  }
  const { data } = await api.post<unknown>("/game/treasure/claim", {
    pointId,
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
  })
  const result = normalizeTreasureClaimResponse(data)
  if (!result) {
    throw new Error("Не удалось забрать клад")
  }
  return result
}

function normalizeBankGeoBody(body: BankGeoBody): BankGeoBody {
  const pointId = String(body.pointId ?? "").trim()
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  if (!pointId || !Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS и точка банка")
  }
  return {
    pointId,
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
  }
}

export const getBankPoints = async (): Promise<BankPoint[]> => {
  const { data } = await api.get<unknown>("/game/bank/points")
  const points = mapBankPoints(data)
  if (__DEV__) {
    console.log(`[bank/points] parsed=${points.length}`)
  }
  return points
}

export const getBankStatus = async (): Promise<BankStatus> => {
  const { data } = await api.get<unknown>("/game/bank/status")
  const status = normalizeBankStatus(data)
  if (!status) {
    throw new Error("Не удалось загрузить состояние банка")
  }
  return status
}

export const getBankCards = async (): Promise<GameCard[]> => {
  const { data } = await api.get<unknown>("/game/bank/cards")
  return normalizeBankCards(data)
}

export const depositBankCoins = async (
  body: BankDepositCoinsBody
): Promise<BankCoinsActionResponse> => {
  const geo = normalizeBankGeoBody(body)
  const amount = Math.floor(Number(body.amount))
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Укажите сумму больше 0")
  }
  const { data } = await api.post<unknown>("/game/bank/deposit-coins", {
    ...geo,
    amount,
  })
  const result = normalizeBankCoinsActionResponse(data)
  if (!result) {
    throw new Error("Не удалось положить монеты в банк")
  }
  return result
}

export const withdrawBankCoins = async (
  body: BankWithdrawCoinsBody
): Promise<BankCoinsActionResponse> => {
  const geo = normalizeBankGeoBody(body)
  const amount = Math.floor(Number(body.amount))
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Укажите сумму больше 0")
  }
  const { data } = await api.post<unknown>("/game/bank/withdraw-coins", {
    ...geo,
    amount,
  })
  const result = normalizeBankCoinsActionResponse(data)
  if (!result) {
    throw new Error("Не удалось забрать монеты из банка")
  }
  return result
}

export const depositBankCard = async (
  body: BankDepositCardBody
): Promise<BankCardActionResponse> => {
  const geo = normalizeBankGeoBody(body)
  const cardId = String(body.cardId ?? "").trim()
  if (!cardId) throw new Error("Не выбрана карта")
  const { data } = await api.post<unknown>("/game/bank/deposit-card", {
    ...geo,
    cardId,
  })
  const result = normalizeBankCardActionResponse(data)
  if (!result) {
    throw new Error("Не удалось положить карту в банк")
  }
  return result
}

export const withdrawBankCard = async (
  body: BankWithdrawCardBody
): Promise<BankCardActionResponse> => {
  const geo = normalizeBankGeoBody(body)
  const cardId = String(body.cardId ?? "").trim()
  if (!cardId) throw new Error("Не выбрана карта")
  const { data } = await api.post<unknown>("/game/bank/withdraw-card", {
    ...geo,
    cardId,
  })
  const result = normalizeBankCardActionResponse(data)
  if (!result) {
    throw new Error("Не удалось забрать карту из банка")
  }
  return result
}

export const upgradeBank = async (
  body: BankGeoBody
): Promise<BankUpgradeResponse> => {
  const geo = normalizeBankGeoBody(body)
  const { data } = await api.post<unknown>("/game/bank/upgrade", geo)
  const result = normalizeBankUpgradeResponse(data)
  if (!result) {
    throw new Error("Не удалось расширить банк")
  }
  return result
}

export const payBankFee = async (
  body: BankGeoBody
): Promise<BankPayFeeResponse> => {
  const geo = normalizeBankGeoBody(body)
  const { data } = await api.post<unknown>("/game/bank/pay-fee", geo)
  const result = normalizeBankPayFeeResponse(data)
  if (!result) {
    throw new Error("Не удалось оплатить долг банка")
  }
  return result
}

export const getBankTermDepositPlans =
  async (): Promise<BankTermDepositPlans> => {
    const { data } = await api.get<unknown>("/game/bank/term-deposit-plans")
    const plans = normalizeBankTermDepositPlans(data)
    if (!plans) {
      throw new Error("Не удалось загрузить тарифы вкладов")
    }
    return plans
  }

export const getBankTermDeposits = async (
  includeClosed = false
): Promise<BankTermDeposit[]> => {
  const { data } = await api.get<unknown>("/game/bank/term-deposits", {
    params: includeClosed ? { includeClosed: true } : undefined,
  })
  return normalizeBankTermDeposits(data)
}

export const openBankTermDeposit = async (
  body: BankOpenTermDepositBody
): Promise<BankTermDepositActionResponse> => {
  const geo = normalizeBankGeoBody(body)
  const amount = Math.floor(Number(body.amount))
  const termDays = Math.floor(Number(body.termDays))
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Укажите сумму больше 0")
  }
  if (![3, 7, 30].includes(termDays)) {
    throw new Error("Выберите срок вклада")
  }
  const { data } = await api.post<unknown>("/game/bank/term-deposits/open", {
    ...geo,
    amount,
    termDays,
  })
  const result = normalizeBankTermDepositActionResponse(data)
  if (!result) {
    throw new Error("Не удалось открыть вклад")
  }
  return result
}

export const claimBankTermDeposit = async (
  depositId: string,
  body: BankGeoBody
): Promise<BankTermDepositActionResponse> => {
  const geo = normalizeBankGeoBody(body)
  const id = String(depositId ?? "").trim()
  if (!id) throw new Error("Вклад не выбран")
  const { data } = await api.post<unknown>(
    `/game/bank/term-deposits/${encodeURIComponent(id)}/claim`,
    geo
  )
  const result = normalizeBankTermDepositActionResponse(data)
  if (!result) {
    throw new Error("Не удалось забрать вклад")
  }
  return result
}

export const earlyCloseBankTermDeposit = async (
  depositId: string,
  body: BankGeoBody
): Promise<BankTermDepositActionResponse> => {
  const geo = normalizeBankGeoBody(body)
  const id = String(depositId ?? "").trim()
  if (!id) throw new Error("Вклад не выбран")
  const { data } = await api.post<unknown>(
    `/game/bank/term-deposits/${encodeURIComponent(id)}/early`,
    geo
  )
  const result = normalizeBankTermDepositActionResponse(data)
  if (!result) {
    throw new Error("Не удалось закрыть вклад досрочно")
  }
  return result
}

export const reorderCards = async (
  body: ReorderCardsBody
): Promise<GameCard[]> => {
  const cardIds = (body.cardIds ?? [])
    .map((id) => String(id ?? "").trim())
    .filter(Boolean)
  if (cardIds.length === 0) {
    throw new Error("Нужен полный список карт рюкзака")
  }
  const { data } = await api.post<unknown>("/game/cards/reorder", { cardIds })
  const cards = normalizeGameCards(data)
  if (cards.length > 0) return cards
  return getCards()
}

function normalizeSortCardsResponse(data: unknown): SortCardsResponse | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null
  const obj = data as Record<string, unknown>
  const nested =
    obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)
      ? (obj.data as Record<string, unknown>)
      : obj
  const sortByRaw = String(nested.sortBy ?? nested.sort_by ?? "")
    .trim()
    .toUpperCase()
  const sortBy: CardSortBy | null =
    sortByRaw === "RANK" ||
    sortByRaw === "BASE_POWER" ||
    sortByRaw === "RANK_THEN_POWER"
      ? sortByRaw
      : null
  if (!sortBy) return null
  const directionRaw = String(nested.direction ?? "desc")
    .trim()
    .toLowerCase()
  const direction: CardSortDirection =
    directionRaw === "asc" ? "asc" : "desc"
  const idsRaw = nested.cardIds ?? nested.card_ids
  const cardIds = Array.isArray(idsRaw)
    ? idsRaw.map((id) => String(id ?? "").trim()).filter(Boolean)
    : []
  return { cardIds, sortBy, direction }
}

export const sortCards = async (
  body: SortCardsBody
): Promise<GameCard[]> => {
  const sortBy = body.sortBy
  if (
    sortBy !== "RANK" &&
    sortBy !== "BASE_POWER" &&
    sortBy !== "RANK_THEN_POWER"
  ) {
    throw new Error("Некорректный тип сортировки")
  }
  const direction: CardSortDirection =
    body.direction === "asc" ? "asc" : "desc"
  const { data } = await api.post<unknown>("/game/cards/sort", {
    sortBy,
    direction,
  })
  const normalized = normalizeSortCardsResponse(data)
  const cardsFromBody = normalizeGameCards(data)
  if (cardsFromBody.length > 0) return cardsFromBody
  if (normalized && normalized.cardIds.length > 0) {
    const current = await getCards()
    const byId = new Map(current.map((card) => [card.id, card]))
    const ordered = normalized.cardIds
      .map((id) => byId.get(id))
      .filter((card): card is GameCard => card != null)
    if (ordered.length > 0) {
      return ordered.map((card, order) => ({ ...card, sortOrder: order }))
    }
  }
  return getCards()
}

export const getOpenMailContracts = async (
  query: MailOpenContractsQuery = {}
): Promise<MailContract[]> => {
  const params: Record<string, string | number> = {}
  if (query.colorRank) params.colorRank = query.colorRank
  if (query.element) params.element = query.element
  if (query.cityId != null) params.cityId = query.cityId
  if (query.roomCode?.trim()) params.roomCode = query.roomCode.trim()
  if (query.creatorPublicCode?.trim()) {
    params.creatorPublicCode = query.creatorPublicCode.trim()
  }
  const { data } = await api.get<unknown>("/game/mail/contracts/open", { params })
  return mapMailContracts(data)
}

export const getMyMailContracts = async (): Promise<MailContractsMineResponse> => {
  const { data } = await api.get<unknown>("/game/mail/contracts/mine")
  return normalizeMailContractsMine(data)
}

export const createMailContract = async (
  body: MailCreateContractBody
): Promise<MailContractActionResult> => {
  const geo = normalizeMailGeoBody(body)
  const { data } = await api.post<unknown>("/game/mail/contracts", {
    ...geo,
    offeredCardId: String(body.offeredCardId),
    wantedElement: body.wantedElement,
    wantedColorRank: body.wantedColorRank,
    expiresAt: mailOpenLotExpiresAt(),
  })
  const result =
    normalizeMailContractActionResult(data) ??
  (() => {
    const contract =
      normalizeMailContract(data) ??
      normalizeMailContract(unwrapGamePayload(data))
    return contract ? { contract } : null
  })()
  if (!result) throw new Error("Не удалось создать лот")
  return result
}

export const acceptMailContract = async (
  contractId: string,
  body: MailAcceptContractBody
): Promise<MailContractActionResult> => {
  const geo = normalizeMailGeoBody(body)
  const { data } = await api.post<unknown>(
    `/game/mail/contracts/${encodeURIComponent(contractId)}/accept`,
    {
      ...geo,
      counterCardId: String(body.counterCardId),
    }
  )
  const result =
    normalizeMailContractActionResult(data) ??
  (() => {
    const contract =
      normalizeMailContract(data) ??
      normalizeMailContract(unwrapGamePayload(data))
    return contract ? { contract } : null
  })()
  if (!result) throw new Error("Не удалось принять лот")
  return result
}

export const cancelMailContract = async (
  contractId: string,
  body: MailGeoBody
): Promise<MailContractActionResult> => {
  const geo = normalizeMailGeoBody(body)
  const { data } = await api.post<unknown>(
    `/game/mail/contracts/${encodeURIComponent(contractId)}/cancel`,
    geo
  )
  const result =
    normalizeMailContractActionResult(data) ??
  (() => {
    const contract =
      normalizeMailContract(data) ??
      normalizeMailContract(unwrapGamePayload(data))
    return contract ? { contract } : null
  })()
  if (!result) throw new Error("Не удалось отменить лот")
  return result
}

export const claimMailContract = async (
  contractId: string,
  body: MailGeoBody
): Promise<MailClaimContractResponse> => {
  const geo = normalizeMailGeoBody(body)
  const { data } = await api.post<unknown>(
    `/game/mail/contracts/${encodeURIComponent(contractId)}/claim`,
    geo
  )
  const result = normalizeMailClaimResponse(data)
  if (!result) throw new Error("Не удалось забрать посылку")
  handleAlmanacStampsFromResponse(data)
  return result
}

export const getCityHallPoint = async (): Promise<CityHallPoint | null> => {
  const { data } = await api.get<unknown>("/game/city-hall/point")
  if (data == null) return null
  const obj = unwrapGamePayload(data)
  if (obj == null) return null
  return normalizeCityHallPoint(obj.point ?? obj)
}

export const getCityHallStatus = async (): Promise<CityHallStatus> => {
  const { data } = await api.get<unknown>("/game/city-hall/status")
  const status = normalizeCityHallStatus(data)
  if (!status) throw new Error("Не удалось загрузить ратушу")
  return status
}

export const getCityHallRaidStatus = async (): Promise<CityHallRaidStatus> => {
  const { data } = await api.get<unknown>("/game/city-hall/raid/status")
  const status = normalizeCityHallRaidStatus(data)
  if (!status) throw new Error("Не удалось загрузить рейд")
  return status
}

export const getCityHallRaidTargets = async (): Promise<CityHallRaidTarget[]> => {
  const { data } = await api.get<unknown>("/game/city-hall/raid/targets")
  return mapCityHallRaidTargets(data)
}

export const voteCityHallRaid = async (
  targetCityId: number
): Promise<CityHallRaidStatus> => {
  const { data } = await api.post<unknown>("/game/city-hall/raid/vote", {
    targetCityId,
  })
  const status = normalizeCityHallRaidStatus(data)
  if (!status) throw new Error("Не удалось проголосовать")
  return status
}

export const setCityHallRaidDefense = async (
  body: CityHallRaidGeoBody
): Promise<CityHallRaidStatus> => {
  const geo = normalizeCityHallRaidGeoBody(body)
  const { data } = await api.post<unknown>("/game/city-hall/raid/defense", geo)
  const status = normalizeCityHallRaidStatus(data)
  if (!status) throw new Error("Не удалось сохранить защиту")
  return status
}

export const clearCityHallRaidDefense = async (): Promise<CityHallRaidStatus> => {
  try {
    const { data } = await api.delete<unknown>("/game/city-hall/raid/defense")
    const status = normalizeCityHallRaidStatus(data)
    if (status) return status
  } catch {
    // fallback: POST empty cardIds
  }
  return setCityHallRaidDefense({ cardIds: [] })
}

export const joinCityHallRaid = async (
  body: CityHallRaidGeoBody
): Promise<CityHallRaidStatus> => {
  const geo = normalizeCityHallRaidGeoBody(body)
  const { data } = await api.post<unknown>("/game/city-hall/raid/join", geo)
  const status = normalizeCityHallRaidStatus(data)
  if (!status) throw new Error("Не удалось вступить в рейд")
  return status
}

export const leaveCityHallRaid = async (): Promise<CityHallRaidStatus> => {
  const { data } = await api.post<unknown>("/game/city-hall/raid/leave")
  const status = normalizeCityHallRaidStatus(data)
  if (!status) throw new Error("Не удалось выйти из рейда")
  return status
}

export const getCityHallRaidDetail = async (
  raidId: string
): Promise<CityHallRaidDetail> => {
  const { data } = await api.get<unknown>(
    `/game/city-hall/raid/${encodeURIComponent(raidId)}`
  )
  const detail = normalizeCityHallRaidDetail(data)
  if (!detail) throw new Error("Не удалось загрузить бой")
  return detail
}

export const getHotelPoints = async (): Promise<HotelPoint[]> => {
  const { data } = await api.get<unknown>("/game/hotel/points")
  const points = mapHotelPoints(data)
  if (__DEV__) {
    console.log(`[hotel/points] parsed=${points.length}`)
  }
  return points
}

export const getHotelStatus = async (): Promise<HotelStatus> => {
  const { data } = await api.get<unknown>("/game/hotel/status")
  const status = normalizeHotelStatus(data)
  if (!status) throw new Error("Не удалось загрузить гостиницу")
  return status
}

export const checkInHotel = async (
  body: HotelCheckInBody
): Promise<HotelCheckInResponse> => {
  const geo = normalizeHotelGeoBody(body)
  const packageSlug = String(body.packageSlug ?? "").trim()
  if (!packageSlug) throw new Error("Выберите номер")
  const { data } = await api.post<unknown>("/game/hotel/check-in", {
    ...geo,
    packageSlug,
  })
  const result = normalizeHotelCheckInResponse(data)
  if (!result) throw new Error("Не удалось заселиться")
  return result
}

export const checkOutHotel = async (): Promise<HotelCheckOutResponse> => {
  const { data } = await api.post<unknown>("/game/hotel/check-out")
  const result = normalizeHotelCheckOutResponse(data)
  if (!result) throw new Error("Не удалось выписаться")
  return result
}

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {}
}

function normalizeWiseTreePoint(raw: unknown): WiseTreePoint | null {
  const o = asRecord(raw)
  const id = String(o.id ?? "")
  if (!id) return null
  return {
    id,
    name: String(o.name ?? "Мудрое дерево"),
    latitude: Number(o.latitude) || 0,
    longitude: Number(o.longitude) || 0,
    radiusMeters: Math.max(1, Math.floor(Number(o.radiusMeters) || 80)),
    maxSlots: Math.max(1, Math.floor(Number(o.maxSlots) || 5)),
    cityId: Math.floor(Number(o.cityId ?? o.city_id) || 0),
    occupiedSlots: Math.max(0, Math.floor(Number(o.occupiedSlots) || 0)),
  }
}

function normalizeWiseTreeStatus(raw: unknown): WiseTreeStatus {
  const o = asRecord(raw)
  const daily = asRecord(o.daily)
  const config = asRecord(o.config)
  const pending = o.pendingRewards ? asRecord(o.pendingRewards) : null
  const occ = o.occupancy ? asRecord(o.occupancy) : null
  const occupantsRaw = Array.isArray(o.occupants) ? o.occupants : []
  return {
    point: normalizeWiseTreePoint(o.point),
    occupancy: occ
      ? {
          pointId: String(occ.pointId ?? ""),
          checkedInAt: String(occ.checkedInAt ?? ""),
          lastHeartbeatAt: String(occ.lastHeartbeatAt ?? ""),
          lastSettledAt: String(occ.lastSettledAt ?? ""),
          isMine: Boolean(occ.isMine),
        }
      : null,
    occupants: occupantsRaw.map((row) => {
      const r = asRecord(row)
      return {
        userId: Math.floor(Number(r.userId) || 0),
        nickname: String(r.nickname ?? "Игрок"),
        affiliationElement: (r.affiliationElement as WiseTreeOccupant["affiliationElement"]) ?? null,
        avatarVariant: Math.floor(Number(r.avatarVariant) || 1),
        checkedInAt: String(r.checkedInAt ?? ""),
        isMe: Boolean(r.isMe),
      }
    }),
    daily: {
      coinsGranted: Math.floor(Number(daily.coinsGranted) || 0),
      chakraGranted: Math.floor(Number(daily.chakraGranted) || 0),
      cardsGranted: Math.floor(Number(daily.cardsGranted) || 0),
      coinsRemaining: Math.floor(Number(daily.coinsRemaining) || 0),
      chakraRemaining: Math.floor(Number(daily.chakraRemaining) || 0),
      cardsRemaining: Math.floor(Number(daily.cardsRemaining) || 0),
    },
    pendingRewards: pending
      ? {
          minutes: Math.floor(Number(pending.minutes) || 0),
          coins: Math.floor(Number(pending.coins) || 0),
          chakra: Math.floor(Number(pending.chakra) || 0),
          cards: Math.floor(Number(pending.cards) || 0),
        }
      : null,
    config: {
      maxSlots: Math.floor(Number(config.maxSlots) || 5),
      coinsPerMinute: Math.floor(Number(config.coinsPerMinute) || 5),
      chakraEveryMinutes: Math.floor(Number(config.chakraEveryMinutes) || 10),
      chakraAmount: Math.floor(Number(config.chakraAmount) || 5),
      cardEveryMinutes: Math.floor(Number(config.cardEveryMinutes) || 40),
      dailyCapCoins: Math.floor(Number(config.dailyCapCoins) || 600),
      dailyCapChakra: Math.floor(Number(config.dailyCapChakra) || 60),
      dailyCapCards: Math.floor(Number(config.dailyCapCards) || 2),
      heartbeatTtlSec: Math.floor(Number(config.heartbeatTtlSec) || 600),
      challengeForfeitSec: Math.floor(Number(config.challengeForfeitSec) || 30),
    },
  }
}

function normalizeWiseTreeGranted(raw: unknown): WiseTreeGranted {
  const o = asRecord(raw)
  const cardsRaw = Array.isArray(o.cards) ? o.cards : []
  return {
    coins: Math.floor(Number(o.coins) || 0),
    chakra: Math.floor(Number(o.chakra) || 0),
    minutes: Math.floor(Number(o.minutes) || 0),
    cards: cardsRaw.map((c) => {
      const row = asRecord(c)
      return {
        id: String(row.id ?? ""),
        colorRank: String(row.colorRank ?? "WHITE"),
        element: String(row.element ?? ""),
      }
    }),
  }
}

export const getWiseTreePoints = async (): Promise<WiseTreePoint[]> => {
  const { data } = await api.get<unknown>("/game/wise-tree/points")
  const list = Array.isArray(data) ? data : []
  return list
    .map(normalizeWiseTreePoint)
    .filter((p): p is WiseTreePoint => p != null)
}

export const getWiseTreeStatus = async (
  pointId?: string
): Promise<WiseTreeStatus> => {
  const { data } = await api.get<unknown>("/game/wise-tree/status", {
    params: pointId ? { pointId } : undefined,
  })
  return normalizeWiseTreeStatus(data)
}

export const checkInWiseTree = async (body: {
  pointId: string
  playerLat: number
  playerLng: number
}): Promise<WiseTreeStatus> => {
  const { data } = await api.post<unknown>("/game/wise-tree/check-in", body)
  return normalizeWiseTreeStatus(data)
}

export const heartbeatWiseTree = async (body: {
  pointId: string
  playerLat: number
  playerLng: number
}): Promise<WiseTreeStatus> => {
  const { data } = await api.post<unknown>("/game/wise-tree/heartbeat", body)
  return normalizeWiseTreeStatus(data)
}

export const claimWiseTree = async (body: {
  pointId: string
  playerLat: number
  playerLng: number
}): Promise<WiseTreeStatus & { granted: WiseTreeGranted }> => {
  const { data } = await api.post<unknown>("/game/wise-tree/claim", body)
  const status = normalizeWiseTreeStatus(data)
  const granted = normalizeWiseTreeGranted(asRecord(data).granted)
  return { ...status, granted }
}

export const checkOutWiseTree = async (body?: {
  pointId?: string
  playerLat?: number
  playerLng?: number
}): Promise<{ granted: WiseTreeGranted; status: WiseTreeStatus }> => {
  const { data } = await api.post<unknown>("/game/wise-tree/check-out", body ?? {})
  const obj = asRecord(data)
  return {
    granted: normalizeWiseTreeGranted(obj.granted),
    status: normalizeWiseTreeStatus(obj.status ?? obj),
  }
}

export const getAirshipHangar = async (): Promise<AirshipHangarPoint | null> => {
  const { data } = await api.get<unknown>("/game/airship/hangar")
  const hangar = normalizeAirshipHangarPoint(data)
  if (__DEV__) {
    console.log(`[airship/hangar] ${hangar ? hangar.id : "null"}`)
  }
  return hangar
}

export const getAirshipStatus = async (): Promise<AirshipStatus> => {
  const { data } = await api.get<unknown>("/game/airship/status")
  const status = normalizeAirshipStatus(data)
  if (!status) throw new Error("Не удалось загрузить статус дирижабля")
  return status
}

export const getAirshipCities = async (): Promise<AirshipCityOption[]> => {
  const { data } = await api.get<unknown>("/game/airship/cities")
  return mapAirshipCities(data)
}

export const getAirshipActiveMissions = async (): Promise<AirshipMapMission[]> => {
  const { data } = await api.get<unknown>("/game/airship/missions/active")
  return mapAirshipMissions(data)
}

export const buyAirship = async (
  body: AirshipGeoBody
): Promise<AirshipBuyResponse> => {
  const geo = normalizeAirshipGeoBody(body)
  const { data } = await api.post<unknown>("/game/airship/buy", geo)
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const status = normalizeAirshipStatus(obj.status ?? data)
  if (!status) throw new Error("Не удалось купить дирижабль")
  const coins =
    typeof obj.coins === "number" && Number.isFinite(obj.coins)
      ? obj.coins
      : undefined
  return { status, coins }
}

export const upgradeAirship = async (
  body: AirshipGeoBody
): Promise<AirshipUpgradeResponse> => {
  const geo = normalizeAirshipGeoBody(body)
  const { data } = await api.post<unknown>("/game/airship/upgrade", geo)
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const status = normalizeAirshipStatus(obj.status ?? data)
  if (!status) throw new Error("Не удалось прокачать дирижабль")
  const coins =
    typeof obj.coins === "number" && Number.isFinite(obj.coins)
      ? obj.coins
      : undefined
  return { status, coins }
}

export const launchAirship = async (
  body: AirshipLaunchBody
): Promise<AirshipLaunchResponse> => {
  const toCityId = Math.floor(Number(body.toCityId))
  if (!Number.isFinite(toCityId) || toCityId <= 0) {
    throw new Error("Выберите город назначения")
  }
  const { data } = await api.post<unknown>("/game/airship/launch", { toCityId })
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const status = normalizeAirshipStatus(obj.status ?? data)
  if (!status) throw new Error("Не удалось отправить дирижабль")
  const energy =
    typeof obj.energy === "number" && Number.isFinite(obj.energy)
      ? obj.energy
      : undefined
  return {
    status,
    mission: status.activeMission,
    energy,
  }
}

export const claimAirship = async (): Promise<AirshipClaimResponse> => {
  const { data } = await api.post<unknown>("/game/airship/claim")
  const result = normalizeAirshipClaimResponse(data)
  if (!result) throw new Error("Не удалось забрать добычу")
  return result
}

export const setAirshipGuards = async (
  body: AirshipGuardsBody
): Promise<AirshipGuardsResponse> => {
  const cardIds = Array.isArray(body.cardIds)
    ? body.cardIds.map((id) => String(id).trim()).filter(Boolean).slice(0, 3)
    : []
  const { data } = await api.post<unknown>("/game/airship/guards", { cardIds })
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const status = normalizeAirshipStatus(obj.status ?? data)
  if (!status) throw new Error("Не удалось сохранить охрану")
  return { status }
}

export const scoutAirshipMission = async (
  missionId: string
): Promise<AirshipMapMission> => {
  const id = String(missionId ?? "").trim()
  if (!id) throw new Error("Нет id миссии")
  const { data } = await api.post<unknown>(`/game/airship/missions/${id}/scout`)
  const mission = normalizeAirshipMapMission(data)
  if (!mission) throw new Error("Не удалось провести разведку")
  return { ...mission, scouted: true }
}

export const attackAirshipMission = async (
  missionId: string,
  attackCardIds: string[]
): Promise<AirshipRaidResult> => {
  const id = String(missionId ?? "").trim()
  const cards = Array.isArray(attackCardIds)
    ? attackCardIds.map((cardId) => String(cardId).trim()).filter(Boolean)
    : []
  if (!id) throw new Error("Нет id миссии")
  if (cards.length < 1 || cards.length > 3) {
    throw new Error("Выберите от 1 до 3 карт для нападения")
  }
  const { data } = await api.post<unknown>(`/game/airship/missions/${id}/attack`, {
    attackCardIds: cards,
  })
  const result = normalizeAirshipRaidResult(data)
  if (!result) throw new Error("Не удалось провести нападение")
  return { ...result, missionId: result.missionId ?? id }
}

export const getAirshipMissionRaids = async (
  missionId: string
): Promise<AirshipRaidHistoryItem[]> => {
  const id = String(missionId ?? "").trim()
  if (!id) throw new Error("Нет id миссии")
  const { data } = await api.get<unknown>(`/game/airship/missions/${id}/raids`)
  return mapAirshipRaids(data)
}

export const getMailMarketLots = async (params: {
  pointId: string
  playerLat: number
  playerLng: number
}): Promise<MailMarketLotsResponse> => {
  const pointId = String(params.pointId ?? "").trim()
  const playerLat = Number(params.playerLat)
  const playerLng = Number(params.playerLng)
  if (!pointId || !Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS для витрины")
  }
  const { data } = await api.get<unknown>("/game/mail/market/lots", {
    params: {
      pointId,
      playerLat: Number(playerLat.toFixed(6)),
      playerLng: Number(playerLng.toFixed(6)),
    },
  })
  return normalizeMailMarketLotsResponse(data)
}

export const getMyMailMarketLots = async (): Promise<MailMarketMineResponse> => {
  const { data } = await api.get<unknown>("/game/mail/market/mine")
  return normalizeMailMarketMineResponse(data)
}

export const createMailMarketLot = async (
  body: MailMarketCreateBody
): Promise<MailMarketActionResponse> => {
  const geo = normalizeMailGeoBody(body)
  const cardId = String(body.cardId ?? "").trim()
  const priceCoins = Math.floor(Number(body.priceCoins))
  if (!cardId) throw new Error("Не выбрана карта")
  if (!Number.isFinite(priceCoins) || priceCoins <= 0) {
    throw new Error("Укажите цену больше 0")
  }
  const { data } = await api.post<unknown>("/game/mail/market/lots", {
    ...geo,
    cardId,
    priceCoins,
  })
  return normalizeMailMarketActionResponse(data)
}

export const cancelMailMarketLot = async (
  lotId: string,
  body: MailGeoBody
): Promise<MailMarketActionResponse> => {
  const geo = normalizeMailGeoBody(body)
  const { data } = await api.post<unknown>(
    `/game/mail/market/lots/${encodeURIComponent(lotId)}/cancel`,
    geo
  )
  return normalizeMailMarketActionResponse(data)
}

export const purchaseMailMarketLot = async (
  lotId: string,
  body: MailGeoBody
): Promise<MailMarketPurchaseResponse> => {
  const geo = normalizeMailGeoBody(body)
  const { data } = await api.post<unknown>(
    `/game/mail/market/lots/${encodeURIComponent(lotId)}/purchase`,
    geo
  )
  const result = normalizeMailMarketPurchaseResponse(data)
  if (!result) throw new Error("Не удалось купить лот")
  handleAlmanacStampsFromResponse(data)
  return result
}

export const getShardsCatalog = async (): Promise<ShardsCatalogResponse> => {
  const { data } = await api.get<unknown>("/game/shards/catalog")
  return normalizeShardsCatalog(data)
}

export const getShardsInventory = async (): Promise<ShardsInventoryResponse> => {
  const { data } = await api.get<unknown>("/game/shards/inventory")
  return normalizeShardsInventory(data)
}

export const craftShard = async (body: {
  element: CardElement
  tier: import("../types/shards").ShardTier
  colorRank: import("../types/battle").ColorRank
}): Promise<CraftShardResponse> => {
  const { data } = await api.post<unknown>("/game/shards/craft", {
    element: body.element,
    tier: body.tier,
    colorRank: body.colorRank,
    color_rank: body.colorRank,
  })
  return normalizeCraftShardResponse(data)
}

export const installShard = async (
  body: {
    cardId: string
    shardId: string
    slotIndex: number
  },
  previousCard?: GameCard
): Promise<GameCard> => {
  const { data } = await api.post<unknown>("/game/shards/install", {
    cardId: body.cardId,
    card_id: body.cardId,
    shardId: body.shardId,
    shard_id: body.shardId,
    slotIndex: body.slotIndex,
    slot_index: body.slotIndex,
  })
  const obj = unwrapGamePayload(data)
  const rawCard = obj.card
  const card =
    normalizeGameCard(rawCard) ??
    normalizeSingleCardResponse(data, body.cardId)
  if (!card) {
    throw new Error("Не удалось установить осколок")
  }
  if (previousCard && previousCard.id === card.id) {
    // Пустой raw → сохранить стихию/стиль с previous (slim DTO без element)
    return mergeGameCardUpdate(previousCard, card, rawCard ?? {})
  }
  return card
}

export const uninstallShard = async (
  body: {
    cardId: string
    slotIndex: number
  },
  previousCard?: GameCard
): Promise<GameCard> => {
  const { data } = await api.post<unknown>("/game/shards/uninstall", {
    cardId: body.cardId,
    card_id: body.cardId,
    slotIndex: body.slotIndex,
    slot_index: body.slotIndex,
  })
  const obj = unwrapGamePayload(data)
  const rawCard = obj.card
  const card =
    normalizeGameCard(rawCard) ??
    normalizeSingleCardResponse(data, body.cardId)
  if (!card) {
    throw new Error("Не удалось снять осколок")
  }
  if (previousCard && previousCard.id === card.id) {
    return mergeGameCardUpdate(previousCard, card, rawCard ?? {})
  }
  return card
}

export const getPetScreen = async (): Promise<PetScreenResponse | null> => {
  const { data } = await api.get<unknown>("/game/pet/")
  return normalizePetScreenResponse(data)
}

export const getPetActiveTime = async (): Promise<PetActiveTimeResponse> => {
  const { data } = await api.get<unknown>("/game/pet/active-time")
  return normalizePetActiveTimeResponse(data)
}

export const awakenPet = async (
  body: PetAwakenBody
): Promise<PetActionResponse> => {
  const { data } = await api.post<unknown>("/game/pet/awaken", {
    element: body.element,
    displayName: body.displayName?.trim() || undefined,
  })
  return normalizePetActionResponse(data)
}

export const renamePet = async (
  displayName: string
): Promise<PetActionResponse> => {
  const { data } = await api.patch<unknown>("/game/pet/rename", {
    displayName: displayName.trim(),
  })
  return normalizePetActionResponse(data)
}

export const respecPet = async (
  body: PetAwakenBody
): Promise<PetActionResponse> => {
  const { data } = await api.post<unknown>("/game/pet/respec", {
    element: body.element,
    displayName: body.displayName?.trim() || undefined,
  })
  return normalizePetActionResponse(data)
}

export const feedPet = async (portion: number): Promise<PetActionResponse> => {
  const { data } = await api.post<unknown>("/game/pet/feed", { portion })
  return normalizePetActionResponse(data)
}

export const choosePetPerk = async (
  body: PetChoosePerkBody
): Promise<PetActionResponse> => {
  const { data } = await api.post<unknown>("/game/pet/choose-perk", body)
  return normalizePetActionResponse(data)
}

export const getCityChronicle = async (
  params: CityChronicleQuery
): Promise<CityChronicleResponse> => {
  const search = new URLSearchParams()
  const limit = params.limit ?? 30
  search.set("limit", String(Math.min(100, Math.max(1, limit))))
  if (params.cursor) search.set("cursor", params.cursor)
  if (params.category) search.set("category", params.category)
  if (params.tier) search.set("tier", params.tier)

  const { data } = await api.get<unknown>(
    `/city/${params.cityId}/chronicle?${search.toString()}`
  )
  return normalizeCityChronicleResponse(data)
}

// ─── Player bases (/game/base) ───────────────────────────────────────────────

export const getBaseConfig = async (): Promise<BaseConfig> => {
  const { data } = await api.get<unknown>("/game/base/config")
  return normalizeBaseConfig(data)
}

export const getMyBase = async (): Promise<BaseMineResponse> => {
  const { data } = await api.get<unknown>("/game/base/mine")
  return normalizeBaseMineResponse(data)
}

export const getBaseHexes = async (params: {
  plane: MapPlane
  minLat: number
  minLng: number
  maxLat: number
  maxLng: number
}): Promise<BaseHexesResponse> => {
  const plane = normalizeMapPlane(params.plane)
  if (!plane) throw new Error("Некорректный слой карты")
  const { data } = await api.get<unknown>("/game/base/hexes", {
    params: {
      plane,
      minLat: Number(params.minLat.toFixed(6)),
      minLng: Number(params.minLng.toFixed(6)),
      maxLat: Number(params.maxLat.toFixed(6)),
      maxLng: Number(params.maxLng.toFixed(6)),
    },
  })
  const result = normalizeBaseHexesResponse(data)
  if (!result) throw new Error("Не удалось загрузить гексы")
  return result
}

export const getBaseMap = async (params: {
  plane: MapPlane
  bounds?: BaseMapBounds | null
}): Promise<BaseMapResponse> => {
  const plane = normalizeMapPlane(params.plane)
  if (!plane) throw new Error("Некорректный слой карты")
  const query: Record<string, string | number> = { plane }
  if (params.bounds) {
    query.minLat = Number(params.bounds.minLat.toFixed(6))
    query.minLng = Number(params.bounds.minLng.toFixed(6))
    query.maxLat = Number(params.bounds.maxLat.toFixed(6))
    query.maxLng = Number(params.bounds.maxLng.toFixed(6))
  }
  const { data } = await api.get<unknown>("/game/base/map", { params: query })
  const result = normalizeBaseMapResponse(data)
  if (!result) throw new Error("Не удалось загрузить базы на карте")
  return result
}

export const claimBase = async (body: BaseClaimBody): Promise<BaseClaimResponse> => {
  const plane = normalizeMapPlane(body.plane)
  if (!plane) throw new Error("Некорректный слой")
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  if (!Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны координаты GPS")
  }
  const { data } = await api.post<unknown>("/game/base/claim", {
    plane,
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
    remote: true,
  })
  const result = normalizeBaseClaimResponse(data)
  if (!result) throw new Error("Не удалось заложить базу")
  return result
}

export const relocateBase = async (
  body: BaseRelocateBody
): Promise<BaseRelocateResponse> => {
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  if (!Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны координаты GPS")
  }
  const { data } = await api.post<unknown>("/game/base/relocate", {
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
    remote: true,
  })
  const result = normalizeBaseRelocateResponse(data)
  if (!result) throw new Error("Не удалось перенести базу")
  return result
}

export const upgradeBase = async (
  body: BaseUpgradeBody
): Promise<BaseUpgradeResponse> => {
  const module = body.module as BaseModuleKind
  if (!module) throw new Error("Выберите модуль")
  const { data } = await api.post<unknown>("/game/base/upgrade", { module })
  const result = normalizeBaseUpgradeResponse(data)
  if (!result) throw new Error("Не удалось улучшить модуль")
  return result
}

export const setBaseGuards = async (
  body: BaseGuardsBody
): Promise<BaseGuardsResponse> => {
  const cardIds = Array.isArray(body.cardIds)
    ? body.cardIds.map((id) => String(id).trim()).filter(Boolean).slice(0, 3)
    : []
  const { data } = await api.post<unknown>("/game/base/guards", { cardIds })
  const result = normalizeBaseGuardsResponse(data)
  if (!result) throw new Error("Не удалось сохранить гарнизон")
  return result
}

export const gatherBase = async (): Promise<BaseGatherResponse> => {
  // Сбор только за энергию (remote); on-site бесплатный сбор отключён.
  const { data } = await api.post<unknown>("/game/base/gather", { remote: true })
  const result = normalizeBaseGatherResponse(data)
  if (!result) throw new Error("Не удалось собрать ресурсы")
  return result
}

export const scoutBase = async (baseId: string): Promise<BaseScoutIntel> => {
  const id = String(baseId ?? "").trim()
  if (!id) throw new Error("Нет id базы")
  const { data } = await api.post<unknown>(`/game/base/${id}/scout`)
  const result = normalizeBaseScoutIntel(data)
  if (!result) throw new Error("Не удалось провести разведку")
  return result
}

export const attackBase = async (
  baseId: string,
  body: BaseAttackBody
): Promise<BaseAttackResult> => {
  const id = String(baseId ?? "").trim()
  const cards = Array.isArray(body.attackCardIds)
    ? body.attackCardIds.map((cardId) => String(cardId).trim()).filter(Boolean)
    : []
  if (!id) throw new Error("Нет id базы")
  if (cards.length < 1 || cards.length > 3) {
    throw new Error("Выберите от 1 до 3 карт для нападения")
  }
  const { data } = await api.post<unknown>(`/game/base/${id}/attack`, {
    attackCardIds: cards,
  })
  const result = normalizeBaseAttackResult(data)
  if (!result) throw new Error("Не удалось провести нападение")
  return result
}
